import React, { forwardRef, useEffect, useRef } from 'react'

import { OmwAvatar, OmwButton, OmwIcon, OmwTooltip } from 'components/ui'

import i18n from 'i18n'
import classNames from 'classnames'
import moment from 'moment'

import FoldableFlow from 'emailThreadViewer/FoldableFlow'

import { applyPseudoStyleScope } from 'libs/style-to-inline'
import { getSafeElement, scrollIntoViewIfNeeded } from 'utils/core'
import { bakePauseGate } from '../helper/bakePauseGate'
import { bodyContentRoot, bodyEventTarget, mountBodyRoot } from './bodyRoot'
import { buildDraftBodyStyleSkeleton } from './draftBodyStyleSkeleton'
import { parseWidthBreakpoints, crossesZone, isRecalcAllowed } from './mediaQueryResize'
import { isMailPerfEnabled, schedulePaintMeasure, type MailPerfMode } from './perf'
import { linkifyMailNode } from '../helper/linkifyMail'
import { handleSchemaEventClick, prewrapOverflowChildren } from '../helper/mailview'
import { upgradeInsecureUrl, upgradeInsecureSrcset } from '../helper/upgradeInsecureImg'
import {
  blobUrlToDataUrl,
  collectConvertibleImgSrcs,
  isBlobInlineImageSrc,
} from './copyInlineImages'
import { getInitialAvatarSrc } from 'utils/ui/core'
import { getUserEmail } from 'emailThreadViewer/helper/utils'
import { getDisplayEmailElem } from 'emailThreadViewer/helper/uiHelpers'
import { getDisplayEmailHTMLStr } from 'emailThreadViewer/helper'

import exclamation24 from 'assets/images/exclamation-circle-24.svg'

import BlockedBanner from 'utils/ui/SecureSend/BlockedBanner'
import ConfidentialBanner from 'utils/ui/SecureSend/ConfidentialBanner'

import Attachment from '../Attachment'

import IFace from '../ViewerIFace'
import type { ReadReceiptInfo, ReadReceiptStatus } from '../ViewerIFace'
import type { PrintPayload } from './printPayload'
import { makeMailBodyIframeSanitizer } from './mailBodyIframeSanitizer'

import ReadReceiptChip from './ReadReceiptChip'

import uiExt from './uiExt'

import styles from './style.module.scss'
// 수신 메일 본문의 Windows 한글 폰트(굴림체 등) macOS 명조 폴백 교정 — OMD-198
// 단일 출처는 common-web(utils/ui/fontAliases.css) — 뷰어 사본 중복 제거.
import 'utils/ui/fontAliases.css'
import {
  TextFindableNode,
  useTextFinderContext,
  type FocusInfo,
} from 'emailThreadViewer/TextFinder'
import { composeRefs } from 'emailThreadViewer/TextFinder/utils/composeRefs'
import Truncate from './Truncate'

import * as planer from 'libs/planer/planer'

import { ClientJS } from 'clientjslite'

// 원격(네트워크 fetch) 이미지 판별 — http(s):// 또는 protocol-relative(//host). shim getSafeHTML 의
// REMOTE_IMG_SRC 와 동일 정의(같은 스킴 계약을 두 레이어가 공유해야 blob:/data: 오차단이 안 생긴다).
// blob:/data:/cid:/resources: 는 로컬·인라인이라 원격 차단 대상이 아니다(차단 배너·pre-blank 제외).
const REMOTE_IMG_SRC = /^(?:https?:)?\/\//i

// 이 결함은 WebKit(Safari·iOS·macOS WKWebView)에서만 발생한다. Blink/Gecko 에선 재지정이 불필요한
// 추가 요청만 되므로, isSafari()(AppleWebKit 엔진 판별)가 true 일 때만 restartBrokenImageLoads 를 건다.
// UA 는 런타임에 안 바뀌므로 모듈 로드 시 한 번만 계산한다(mailview.ts SAFARI_SCROLLW_BUFFER 와 동일 패턴).
const IS_WEBKIT_ENGINE = new ClientJS().isSafari()

// macOS 27 WebKit: getSafeElement 의 임시 bake iframe 을 제거할 때, 그 iframe 문서에서 시작된 아직
// 끝나지 않은 <img> 로드가 실패 상태로 남고 복구되지 않는다(이전 WebKit 은 정상). bake 된 fragment 를
// 뷰어 문서에 붙인 "직후" 깨진 이미지의 src/srcset 을 다시 지정해 뷰어 문서에서 로드를 새로 시작한다.
// - owa-src(차단)·owa-cid(미해결 cid)는 각자 복원 경로(applyBlockedImg/applyInlineImgList)가 src 를
//   지정하므로 건드리지 않는다.
// - 이미 정상 로드된 이미지(complete && naturalWidth>0)는 제외한다 — 재지정 시 두 번째 네트워크 요청
//   (추적 픽셀 중복 집계·blob 깜빡임)이 되기 때문. temp iframe 은 이 시점에 이미 제거돼, 끊긴 로드는
//   complete=true 이면서 naturalWidth===0 으로 남으므로 이것만 재시작한다.
// ⚠ applyBlockedImg/applyInlineImgList 가 fresh src 를 넣기 "전"(appendChild 직후)에 호출해야, 방금
//   지정된 src 를 깨진 것으로 오판해 이중 요청하지 않는다.
// NOTE: <picture><source srcset> 은 아직 미처리 — 차단/허용 로직과 함께 별도 작업으로 다룬다.
export const restartBrokenImageLoads = (root: ParentNode): void => {
  root.querySelectorAll('img').forEach((img) => {
    if (img.hasAttribute('owa-src') || img.hasAttribute('owa-cid')) return
    if (img.complete && img.naturalWidth > 0) return
    for (const name of ['srcset', 'src'] as const) {
      const value = img.getAttribute(name)
      if (value == null || value === '') continue
      img.removeAttribute(name)
      img.setAttribute(name, value)
    }
  })
}

// OMD-28: 본문(.htmlMailBody)의 실제 content box 폭을 getSafeElement width 인자로 넘길 때 쓰는 보정값.
// 폭 소스는 항상 마운트되어 있는 `.container`(containerRef)이며, 본문이 렌더되는 `.viewContainer`는
// getSafeElement 결과(state)로만 렌더되어 호출 시점엔 존재하지 않으므로 상수로 보정한다.
// 88 = `.viewContainer` padding(0 32px 16px 56px)의 좌 56 + 우 32. `.container`는 overflow:hidden 이라
// clientWidth = offsetWidth − border 이고, 중간 `.mailBody` 래퍼는 좌우 padding/border 가 없다.
const VIEW_CONTAINER_HORIZONTAL_PADDING = 88

interface EmailAddress {
  name: string
  email: string
}

interface MailInfo {
  id: string
  messageId?: string
  draft?: boolean
  sender?: EmailAddress
  from: EmailAddress
  to?: EmailAddress[]
  cc?: EmailAddress[]
  bcc?: EmailAddress[]
  subject?: string
  summary?: string
  read?: boolean
  body?: BodyData
  received_time: number
  quoted_visible?: boolean
  has_attachment?: boolean
  enable_flag?: boolean
  priority?: string
  sensitivity?: string
  flagged?: boolean
  /** 플래그 토글 모양 — Gmail 계정은 'star'(별표). 미지정 = 'flag' (desktop gmail-star-display). */
  flag_style?: 'flag' | 'star'
  priority_inbox?: string
  is_scheduled_email?: boolean
  ai_summary?: string
  enable_reply_forward?: boolean
  enable_read?: boolean
  enable_delete?: boolean
  enable_print?: boolean
  enable_open_in_new_window?: boolean
  enable_redownload?: boolean
  is_text_partial?: boolean
  // thread 보기에서 현재 메일함과 다른 메일함에 속한 메일의 메일함 표시 이름.
  mailbox_name?: string
  // mailbox_name 의 전체 경로명(부모/자식). hover 시 tooltip 으로 표시한다.
  mailbox_fullname?: string
  // parsed-body-doc-from-viewer(Gate B·정책): 본문 표시 파싱의 원본 보존 clone 을 host 에
  // 캡처 보고해도 되는 메일인지. host 가 판단해 실어 보낸다(미지정=false — 캡처 비활성).
  enable_capture_element?: boolean
}

// parsed-body-doc-from-viewer: doc 캡처의 크기 실익 하한(256KB). 파싱 비용은 본문 크기에
// 비례(~1.5ms/KB)하므로 이 미만은 캡처 이득이 미미하고, 작은 메일이 host 캐시 FIFO 에서
// 대형 엔트리를 밀어내는 것도 막는다.
const CAPTURE_MIN_BODY_LEN = 262144

interface InlineImage {
  cid: string
  url: string
  id?: string
}

interface AttachmentObj {
  id: string
  name?: string
  size: number
  mime_type: string
  enable_preview?: boolean
  enable_open?: boolean
  enable_save_as?: boolean
}

interface BodyData {
  type: string
  body: string
  attachments?: AttachmentObj[]
  inline_images?: InlineImage[]
  meeting_info?: MeetingInfo
}

interface MeetingInfo {
  dt_start: number
  dt_end: number
  type: number
  timezone?: string
  location?: string
  sender: EmailAddress
  title: string
  new_start_propose_time?: number
  new_end_propose_time?: number
  propose_comment?: string
  enable_response?: boolean
  meeting_response_actions?: number[]
  meeting_proposal_accepted?: boolean
  recurrence?: string
  sequence?: number
}

interface ReactMgr {
  addReactObj: (obj: any) => void
  removeReactObj: (obj: any) => void
}

// EmailView 내부에서 host window 의 docUtil 글로벌 (mailview.ts startMailView 가 등록)
// 에 접근하기 위한 inline narrowing type. mailview.ts 의 MailViewWindow 와 동일 source
// 이지만 viewer 패키지의 다른 영역과 type 충돌을 회피하기 위해 local 정의.
type WindowWithDocUtil = Window & {
  docUtil?: {
    addViewElement: (id: string, elem: HTMLElement | undefined) => void
    // OMD-29: 재계산으로 본문 DOM 교체 시 다크모드 재적용에 사용.
    isDarkMode: () => boolean
    applyDarkMode: () => void
    // 인쇄 payload 생성 시 다크 색을 원복하기 위해 접근하는 공유 colorMgr(EditorMgr).
    // _restoreElemColor 는 data-nine-* 속성·인라인 색을 원래 색으로 되돌린다(in-place).
    colorMgr: { _restoreElemColor: (elem: HTMLElement) => void }
    // F12: 부분 zoom 펼치기 버튼 원본 — 선감싼 container 에 layout 전 복제해 둔다.
    expandIconElem?: HTMLElement | null
    // §15.14-②: 인라인 이미지 선변환 결과를 공유 map(EditorMgr)에 push / 조회.
    setInlineDataUrl: (url: string, dataUrl: string) => void
    getInlineDataUrl: (src: string) => string | null
    mailViewMap: Record<
      string,
      {
        notifyVisible: () => void
        // F12: 부분 zoom 이 켜져 있는지 — 꺼져 있으면 본문을 미리 감싸지 않는다.
        partialZoomMode: boolean
        checkOption: (opts: { mode: string }) => void
      }
    >
  }
}

// CSS @page 는 비표준 CSSStyleDeclaration property (print 영역). element.style.page
// 직접 할당 패턴을 좁힘.
type StyleWithPage = CSSStyleDeclaration & { page?: string }

interface ThreadItem {
  mailInfo: MailInfo
  reactMgr: ReactMgr
  reactObj?: EmailView
  reqBody: () => void
  setRead: (read: boolean) => void
  setFlagged: (flagged: boolean) => void
}

interface EmailViewProps {
  threadItem: ThreadItem
  fold?: boolean
  hidden?: boolean
  selected?: boolean
  isFirst?: boolean
  hasTitle?: boolean
  owner?: any
  bodySearch?: boolean
}

interface EmailViewState {
  hasExternalImg: boolean | null
  isBlockedRes: boolean | null
  infoElem: React.ReactNode | null
  meetingInfo: MeetingInfo | null
  bodyElem: React.ReactNode | null
  fullBodyElem: React.ReactNode | null
  isDraft: boolean
  foldAttachment: boolean
  attachmentMinCnt: number
  allFileSize: number
  isExpandEnabled: boolean
  isFold: boolean | null
  isQuotedVisible: boolean
  hasQuoted: boolean | null
  actionsWidth: number | null
  isLess400: boolean | null
  avatar?: string
  attachments?: React.ReactNode[]
  isSecureMail: boolean
  secureSendExpired: string | null
  secureMailId: string | null
  isReadReceipt: boolean
  readReceiptStatus: ReadReceiptStatus | null
  receiptId: string | null
}

interface ObserverObj {
  element?: HTMLElement
  ref?: HTMLElement
  onClick?: (e: MouseEvent) => void
  onDblClick?: (e: MouseEvent) => void
  clear: () => void
}

interface AttachmentsObserver {
  element?: HTMLElement
  ref?: HTMLElement
  resizeTimer?: number
  calcHeight: () => void
  observer?: ResizeObserver
  clear: () => void
}

interface ContainerObserver {
  element?: HTMLElement
  ref?: HTMLElement
  resizeTimer?: number
  onResize: () => void
  observer?: ResizeObserver
  clear: () => void
}

export class EmailView extends React.Component<EmailViewProps, EmailViewState> {
  isBlockedRes: boolean | null = null
  isAllowedSender = false
  inlineImgList: InlineImage[] = []
  existInlineImgMap: Record<string, InlineImage> = {}
  // 외부 이미지 허용 판정(checkAllowedAndKnownSender)이 끝나기 전(isBlockedRes === null)에
  // 본문이 먼저 도착하면, applyBodyElem 이 `isBlockedRes !== false` 조건으로 정상 외부 이미지까지
  // 차단해버린다(그리고 setResourceBlocked(null) 은 no-op 이라 방치됨). render→isFold→reqDetails 가
  // 판정과 병렬로 본문을 가져오기 때문에 느린 환경에서 이 순서가 뒤집힌다. 판정 확정까지 html 본문을
  // 여기 보류했다가 setResourceBlocked 에서 재개해, 외부 이미지가 항상 확정된 isBlockedRes 로 처리되게 한다.
  pendingBlockResBody: { body: string; inlineImages?: InlineImage[] } | null = null
  isReqBody = false
  isQuotedVisible = false
  isUnmounted = false
  // OMD-131: 인용 전체 본문의 bake 입력(원본 HTML)과 1회 실행 래치. `showQuoted` 까지 지연된다.
  pendingFullBodyHTML?: string
  fullBodyBakeStarted = false
  // 진행 중인 bake 의 완료(= setState commit) promise. 인쇄가 bake 도중에 끼어들어도 같은 1회를
  // 기다리게 한다(`bakeFullBody` 반환값).
  fullBodyBakePromise: Promise<void> | null = null
  checkSenderTimeoutId: ReturnType<typeof setTimeout> | null = null
  secureSendExpireTimerId: ReturnType<typeof setTimeout> | null = null
  isExpandEnabled = false
  divFrameMap: Record<string, HTMLElement> = {}
  attachmentProgressMap: Record<string, (err: any, progress: any) => void> = {}
  // OMD-160/§15.14-②: 본문 복사 시 인라인 이미지(blob:/officemail-inline://)를 self-contained data URL
  // 로 담기 위한 선변환. 선택 시작 시점에 미리 변환해 EditorMgr 공유 map(SSOT)에 push 하고, copy 는
  // DocUtil.processCopy 가 그 map 을 동기 조회해 치환한다. pending 은 이 뷰의 in-flight 변환 dedup.
  inlineDataUrlPending: Set<string> = new Set()
  boundBodyElems: HTMLElement[] = []
  observerObj?: ObserverObj
  attachmentsObserver?: AttachmentsObserver
  containerObserver?: ContainerObserver
  containerRef?: HTMLElement
  prevLess400?: boolean
  prevRefWidth?: number
  loading?: boolean
  // OMD-29: 리사이즈 시 media query 재반영(B안). 재계산 입력(원본)과 breakpoint·크기 지표를 보관한다.
  // 원본은 반응형(width breakpoint 보유) 메일일 때만 유지해 메모리 부담을 반응형 케이스로 한정한다.
  originalBody?: string
  originalInlineImages?: InlineImage[]
  mediaBreakpoints: number[] = []
  bodyElementCount = 0
  lastBakeMs: number | null = null
  prevZoneWidth?: number
  // 계측 하네스용(기본 비활성). reqBody 디스패치 시각·fetch 소요·본문 div commit 시각을 보관한다.
  perfReqStart?: number
  perfFetchMs = 0
  perfBodyCommitAt?: number
  // getSafeElement 가 반환한 본문 DOM(fragment). setDivBody 의 ref 콜백에서 ref.appendChild 로
  // 삽입한다. 삽입(소비) 후 undefined 로 비워 재렌더 시 중복 삽입을 막는다. 리사이즈 재계산 시엔
  // 새 fragment 가 들어와 기존 내용을 비우고 재삽입한다.
  bodyFragment?: DocumentFragment
  fullBodyFragment?: DocumentFragment
  // 진행 중인 본문 변환(getSafeElement)을 중단하기 위한 컨트롤러. 뷰 언마운트(다른 메일로 이동) 시
  // abort 하여, 뒤늦게 도착한 본문의 비싼 변환이 메인 스레드를 블록(freeze)하지 않도록 한다.
  bakeController = new AbortController()

  constructor(props: EmailViewProps) {
    super(props)

    this.state = {
      hasExternalImg: null,
      isBlockedRes: null,
      infoElem: null,
      meetingInfo: null,
      bodyElem: null,
      fullBodyElem: null,
      isDraft: false,
      foldAttachment: true,
      attachmentMinCnt: 0,
      allFileSize: 0,
      isExpandEnabled: false,
      isFold: null,
      isQuotedVisible: false,
      hasQuoted: null,
      actionsWidth: null,
      isLess400: null,
      isSecureMail: false,
      secureSendExpired: null,
      secureMailId: null,
      isReadReceipt: false,
      readReceiptStatus: null,
      receiptId: null,
    }
  }

  componentDidMount() {
    const { threadItem } = this.props
    const { mailInfo, reactMgr } = threadItem

    // StrictMode(React19 dev) 재마운트 안전: mount→unmount→mount 를 같은 인스턴스로 시뮬레이션할
    // 때 componentWillUnmount 가 isUnmounted=true 로 두는데, 재-mount 시 리셋하지 않으면
    // checkAllowedAndKnownSender(첫 줄 isUnmounted 가드) 와 in-flight promise 가 전부 조기
    // return 되어 외부 이미지 프롬프트(resInfo)·본문 요청이 영구 미설정된다.
    this.isUnmounted = false
    // StrictMode 재마운트 시 unmount 에서 abort 된 컨트롤러로는 이후 모든 본문 변환(getSafeElement)
    // 이 즉시 AbortError 로 끝나므로, 재-mount 시 새 컨트롤러로 교체한다.
    if (this.bakeController.signal.aborted === true) {
      this.bakeController = new AbortController()
    }

    i18n.on('languageChanged', this.onLanguageChanged)

    reactMgr.addReactObj(this)

    this.initData()

    if (threadItem != null) {
      // details 요청 후 받은 데이터를 처리하기 위해 필요.
      threadItem.reactObj = this

      if (mailInfo.draft === true) {
        this.setResourceBlocked(false)

        this.setResInfo({
          email: mailInfo.sender != null ? mailInfo.sender.email : '',
        })

        this.reqDetails()
      } else {
        this.checkAllowedAndKnownSender()
      }
    }
  }

  componentDidUpdate(prevProps: EmailViewProps) {
    const { fold, hidden } = this.props

    if (prevProps != null && (prevProps.fold !== fold || prevProps.hidden !== hidden)) {
      if (prevProps.fold !== fold) {
      }
      const { isFold } = this.state
      if (isFold != null) {
        this.setState({
          isFold: null,
        })
      } else {
        this.forceUpdate()
      }
    }
  }

  componentWillUnmount() {
    const { threadItem } = this.props
    const { reactMgr } = threadItem

    this.isUnmounted = true
    // 진행 중/대기 중인 본문 변환 중단 — 다른 메일로 이동 후 뒤늦은 변환이 freeze 를 유발하지 않게 한다.
    this.bakeController.abort()
    if (this.checkSenderTimeoutId != null) {
      clearTimeout(this.checkSenderTimeoutId)
      this.checkSenderTimeoutId = null
    }
    if (this.secureSendExpireTimerId != null) {
      clearTimeout(this.secureSendExpireTimerId)
      this.secureSendExpireTimerId = null
    }

    reactMgr.removeReactObj(this)

    i18n.off('languageChanged', this.onLanguageChanged)
    if (this.observerObj != null) {
      this.observerObj.clear()
    }

    if (this.attachmentsObserver != null) {
      this.attachmentsObserver.clear()
    }

    if (this.containerObserver != null) {
      this.containerObserver.clear()
      this.containerObserver.element = undefined
      this.containerObserver.ref = undefined
    }

    // threadItem(EmailData)이 외부(window.singleUse 콜백 등)에서 살아남아도
    // unmount된 인스턴스와 detached 본문 div가 함께 유지되지 않도록 참조를 끊는다.
    if (threadItem != null && threadItem.reactObj === this) {
      threadItem.reactObj = undefined
    }
    // OMD-160: 본문 복사 리스너 해제 + data URL 맵/pending 정리.
    this.unbindBodyClipboard()
    this.divFrameMap = {}
    this.attachmentProgressMap = {}
    this.inlineImgList = []
    this.existInlineImgMap = {}
    this.containerRef = undefined
    // OMD-131: 인용 본문 원본 HTML(대형 메일은 수백 KB)을 unmount 시 놓아준다.
    this.pendingFullBodyHTML = undefined
  }

  reset = () => {
    this.isReqBody = false
  }

  initData = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const { quoted_visible: quotedVisible } = mailInfo

    this.isQuotedVisible = quotedVisible === true

    this.divFrameMap = {}

    this.applySender(true)
  }

  applySender = (_init?: boolean) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const { from } = mailInfo

    const sender = from

    if (sender != null && sender.email != null && sender.email.trim().length > 0) {
      const avatar = getInitialAvatarSrc(sender.name, sender.email)

      this.setState({
        avatar,
      })

      // 브릿지 미초기화/해제 타이밍 레이스 가드(M6 sibling). getAvatar 부재 시 skip해도
      // 위에서 setState한 이니셜 아바타(getInitialAvatarSrc)가 유지되어 안전 degradation.
      const getAvatar = IFace.WebToNative.getAvatar
      if (typeof getAvatar === 'function') {
        getAvatar(sender.email, sender.name).then((ret: any) => {
          const { url, type, data } = ret
          if (url != null) {
            this.setState({
              avatar: url,
            })
          } else if (data != null && type != null) {
            const dataUrl = `data:${type};base64,${data}`
            this.setState({
              avatar: dataUrl,
            })
          }
        })
      }
    }
  }

  // host 가 notifyEmailChanged({ avatar })로 직접 전달한 아바타를 그대로 반영한다. 계정 색 override
  // 변경처럼 발신자 email 이 그대로여서 applySender 재요청 트리거(from.email 변경)에 안 걸리는 경우를
  // 위한 경로 — host 가 리스트와 같은 색으로 산출한 url 을 밀어넣으면 getAvatar 왕복 없이 즉시 바꾼다.
  applyAvatar = (avatar: string) => {
    if (typeof avatar === 'string' && avatar.length > 0) {
      this.setState({ avatar })
    }
  }

  onDraftChanged = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    this.setResInfo({
      email: mailInfo.sender != null ? mailInfo.sender.email : '',
    })
  }

  reqDetails = (forced?: boolean) => {
    const { fold, threadItem } = this.props

    if (this.isReqBody !== true && (fold === false || forced === true)) {
      this.isReqBody = true

      this.loading = true

      // reqDetails 는 render()(isFold()) 안에서 불리므로 렌더 도중 setState 를 피하려는
      // 1프레임 유예만 필요하다. 앞의 setTimeout(20)은 그 위에 얹힌 중복 대기였다 — 제거.
      requestAnimationFrame(() => {
        this.perfReqStart = performance.now()
        threadItem.reqBody()
      })
    }
  }

  isFold = (): boolean => {
    const { fold } = this.props
    const { isFold } = this.state
    let ret: boolean
    if (isFold == null) {
      ret = fold != null ? fold : false
    } else {
      ret = isFold
    }

    if (ret === false) {
      this.reqDetails(true)
    }

    return ret
  }

  /**
   * `options.markRead === false` 면 펼침이 **읽음 처리를 하지 않는다**. 기본(옵션 없음)은 종전대로
   * 첫 펼침에서 `setRead(true)` 다 — 사용자가 직접 펼친 것은 열람이기 때문이다. 검색 결과에서
   * 매치 메일을 프로그램이 펼치는 경로(`EmailContainer.expandEmails`)는 열람이 아니므로 이 옵션을
   * 쓴다. 안 그러면 검색 hit 를 여는 것만으로 형제 unread 메일이 전부 읽음이 되고, "읽지 않음"
   * 필터 검색에서는 결과 행 자체가 사라진다.
   */
  setFold = (fold: boolean, notifyOwner?: boolean, options?: { markRead?: boolean }) => {
    const { isFold } = this.state

    // 변경 1a — 펼침 부수효과 멱등화: 이미 펼쳐진 상태(isFold === false)에서 재호출되면
    // 펼침 부수효과(notifyVisible 등)를 반복하지 않는다. notifyVisible → notifyResized →
    // 리렌더 → 재포커스로 이어지는 무한 루프(고리 A)를 차단한다.
    // 판단은 state.isFold로만 한다. isFold()는 props.fold 폴백 때문에 최초 펼침을 오판한다.
    if (fold === false && isFold !== false) {
      const { threadItem } = this.props
      const { mailInfo } = threadItem

      const { read, id } = mailInfo

      if (read !== true && options?.markRead !== false) {
        threadItem.setRead(true)
      }

      if (
        (window as WindowWithDocUtil).docUtil != null &&
        (window as WindowWithDocUtil).docUtil.mailViewMap[id] != null
      ) {
        ;(window as WindowWithDocUtil).docUtil.mailViewMap[id].notifyVisible()
      }
    }

    if (notifyOwner !== false && isFold !== fold) {
      const { owner, threadItem } = this.props
      owner.notifyEmailFoldChanged(threadItem, fold)
    }

    // 변경 1b — setState 멱등화: 동일 값(false→false) setState도 비 PureComponent 클래스에서는
    // 리렌더를 유발하고, 인라인 onFocusChange와 결합 시 리렌더 루프(고리 B)의 트리거가 된다.
    // (React의 same-value bailout은 useState 훅 전용으로 this.setState에는 적용되지 않는다)
    if (isFold !== fold) {
      this.setState({
        isFold: fold,
      })
    }
  }

  onClickContainer = () => {
    const { threadItem, owner } = this.props
    const { mailInfo } = threadItem

    if (owner != null && owner.setSelectedInner) {
      owner.setSelectedInner(mailInfo.id)
    }

    // native 인터페이스(WebToNative) 초기화(initIFace) 전 호출 시 함수 부재 (OFFICEMAIL-WEB-ACCESS-M7)
    if (typeof IFace.WebToNative?.notifySelect === 'function') {
      IFace.WebToNative.notifySelect(mailInfo.id)
    }
  }

  onClickHeader = (e: React.MouseEvent) => {
    // Radix(OmwDropdownMenu) 메뉴 아이템은 portal 로 렌더되지만 React 는 portal 이벤트를 React
    // 트리로 버블시켜, 메뉴 선택(예: 안읽음표시) 클릭이 여기까지 올라와 fold 를 토글하던 회귀
    // (antd Dropdown 에는 없던 antd→Radix 치환 부작용). 클릭 target 이 header DOM 밖(portal)이면
    // click-through 로 간주해 무시한다.
    const target = e.target as Node
    if (e.currentTarget instanceof HTMLElement && !e.currentTarget.contains(target)) {
      return
    }
    const emailElem = (e.target as HTMLElement).closest('.emailSet[data-email]')
    if (emailElem != null) {
      const email = emailElem.getAttribute('data-email')
      const name = emailElem.getAttribute('data-name')
      IFace.WebToNative.showContact(email, name)

      e.preventDefault()
      e.stopPropagation()
      return
    }

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0 && selection.getRangeAt(0).collapsed !== true) {
      return
    }

    const newFold = !this.isFold()
    this.setFold(newFold)
  }

  onClickAvatar = (e: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem
    const { from } = mailInfo
    const { email, name } = from

    IFace.WebToNative.showContact(email, name)

    e.preventDefault()
    e.stopPropagation()
  }

  checkAllowedAndKnownSender = (retryCount: number = 0) => {
    if (this.isUnmounted) return

    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const { from, messageId } = mailInfo

    const sender = from

    if (sender == null || sender.email == null) {
      this.setResourceBlocked(true)
      this.reqDetails()
      return
    }

    const TIMEOUT_MS = 5000
    let settled = false

    if (this.checkSenderTimeoutId != null) {
      clearTimeout(this.checkSenderTimeoutId)
    }

    this.checkSenderTimeoutId = setTimeout(() => {
      this.checkSenderTimeoutId = null
      if (settled || this.isUnmounted) return
      settled = true

      if (retryCount < 2) {
        this.checkAllowedAndKnownSender(retryCount + 1)
      } else {
        console.warn('checkAllowedAndKnownSender timeout after retries')
        this.setResourceBlocked(true)
        this.reqDetails()
      }
    }, TIMEOUT_MS)

    IFace.WebToNative.checkAllowedAndKnownSender(messageId, sender.email)
      .then((res: { isBlocked: boolean; isAllowedSender: boolean | null }) => {
        if (settled || this.isUnmounted) return
        settled = true
        if (this.checkSenderTimeoutId != null) {
          clearTimeout(this.checkSenderTimeoutId)
          this.checkSenderTimeoutId = null
        }

        const { isBlocked, isAllowedSender } = res

        if (isAllowedSender != null) {
          this.isAllowedSender = isAllowedSender
        }

        if (isBlocked === false) {
          this.setResourceBlocked(false)
        } else {
          this.setResourceBlocked(true)
          if (isAllowedSender === false) {
            this.setResInfo({
              email: sender.email,
            })
          }
        }

        this.reqDetails()
      })
      .catch(() => {
        if (settled || this.isUnmounted) return
        settled = true
        if (this.checkSenderTimeoutId != null) {
          clearTimeout(this.checkSenderTimeoutId)
          this.checkSenderTimeoutId = null
        }

        if (retryCount < 2) {
          this.checkAllowedAndKnownSender(retryCount + 1)
        } else {
          console.warn('checkAllowedAndKnownSender error after retries')
          this.setResourceBlocked(true)
          this.reqDetails()
        }
      })
  }

  setResourceBlocked = (isBlocked: boolean) => {
    if (this.isBlockedRes !== isBlocked) {
      this.isBlockedRes = isBlocked
      if (isBlocked === false) {
        Object.values(this.divFrameMap).forEach((divElem) => {
          this.applyBlockedImg(bodyContentRoot(divElem))
        })
      }
      this.setState({
        isBlockedRes: this.isBlockedRes,
      })
    }
    // 판정 확정 전 도착해 보류됐던 본문이 있으면 지금 렌더한다(확정된 isBlockedRes 로 외부 이미지 처리).
    if (this.pendingBlockResBody != null && this.isBlockedRes !== null) {
      const pending = this.pendingBlockResBody
      this.pendingBlockResBody = null
      this.applyBodyElem(pending.body, pending.inlineImages)
    }
  }

  onLanguageChanged = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    this.setResInfo({ email: mailInfo.from.email })
  }

  setResInfo = (info: { email: string }) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    let infoElem: React.ReactNode = null
    if (mailInfo.draft !== true) {
      infoElem = (
        <div className={classNames(styles.resInfo)}>
          <div className={classNames(styles.icon)}>
            <img src={exclamation24} />
          </div>

          <div className={classNames(styles.content)}>
            <div className={classNames(styles.desc1)}>{i18n.t('email.desc.safeSender')}</div>
            <div className={classNames(styles.desc2)}>
              {this.isAllowedSender !== true && (
                <a href="" onClick={(e) => this.addTrustEmail(e, info.email)}>
                  {i18n.t('email.desc.trustSender', { email: info.email })}
                </a>
              )}

              <a
                href=""
                onClick={(e) => {
                  this.unblockRes(e)
                }}
              >
                {i18n.t('email.desc.showBlocked')}
              </a>
            </div>
          </div>
        </div>
      )
    }

    this.setState({ infoElem, isDraft: mailInfo.draft === true })
  }

  addTrustEmail = (e: React.MouseEvent, email: string) => {
    IFace.WebToNative.addAllowedSender(email).then(() => {
      this.setResourceBlocked(false)
    })

    e.preventDefault()
    e.stopPropagation()
  }

  unblockRes = (e: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    IFace.WebToNative.addTempAllowedEmailId(mailInfo.id)

    this.setResourceBlocked(false)

    e.preventDefault()
    e.stopPropagation()
  }

  onSetDetails = (bodyData: BodyData) => {
    const { threadItem, isFirst } = this.props
    const { mailInfo } = threadItem

    // 계측: reqBody 디스패치 → 본문 데이터 도착(fetch 구간).
    if (this.perfReqStart != null) {
      this.perfFetchMs = performance.now() - this.perfReqStart
    }

    let bodyElem: React.ReactNode = null

    const {
      type,
      body,
      attachments,
      inline_images: inlineImages,
      meeting_info: meetingInfo,
    } = bodyData

    if (inlineImages != null) {
      this.onSetInlineImages(inlineImages)
    }

    let newState: Partial<EmailViewState> = {}

    if (attachments != null) {
      let allFileSize = 0

      const attachmentElems = attachments.map((attachmentObj) => {
        const attrs: any = {
          attachmentObj,
          downloadable: true,
          enableDelete: false,
          api: {
            setProgressCB: (onProgressCB: (err: any, progress: any) => void) => {
              this.attachmentProgressMap[attachmentObj.id] = onProgressCB
            },
            download: (onDownload: (ret: any) => void) => {
              IFace.WebToNative.download(mailInfo.id, attachmentObj.id).then((ret: any) => {
                console.log('download - ', ret)
                if (onDownload != null) {
                  onDownload(ret)
                }
              })
            },
            cancelDownload: () => {
              IFace.WebToNative.cancelDownload(mailInfo.id, attachmentObj.id)
            },
            downloadFile: () => {
              IFace.WebToNative.downloadFile(mailInfo.id, attachmentObj.id)
            },
            showPreview: () => {
              IFace.WebToNative.showPreview(mailInfo.id, attachmentObj.id)
            },
            open: () => {
              IFace.WebToNative.open(mailInfo.id, attachmentObj.id)
            },
            saveAs: () => {
              IFace.WebToNative.saveAs(mailInfo.id, attachmentObj.id)
            },
            saveAndOpen: () => IFace.WebToNative.saveAndOpen(mailInfo.id, attachmentObj.id),
            showInFolder: () => IFace.WebToNative.showInFolder(mailInfo.id, attachmentObj.id),
          },
        }

        const attachmentElem = <Attachment key={`${attachmentObj.id}`} {...attrs} />
        allFileSize += attachmentObj.size

        return attachmentElem
      })

      newState = {
        ...newState,
        attachments: attachmentElems,
        allFileSize,
      }
    }

    if (meetingInfo != null) {
      newState = {
        ...newState,
        isBlockedRes: false,
        meetingInfo,
      }
    } else {
      newState = {
        ...newState,
        meetingInfo: null,
      }
    }

    if (type === 'text/html') {
      if (this.isBlockedRes === null) {
        // 허용 판정 미완료 — 지금 렌더하면 정상 외부 이미지까지 차단된다. 판정 확정까지 보류하고
        // setResourceBlocked 에서 재개한다. (판정은 성공/타임아웃/에러 모든 경로에서 반드시 확정됨.)
        this.pendingBlockResBody = { body, inlineImages }
      } else {
        this.applyBodyElem(body, inlineImages)
      }
    } else {
      if (type === 'text/plain') {
        let bodyText = body
        let fullBodyElem: React.ReactNode = null
        if (this.isQuotedVisible === false && isFirst !== true) {
          const fullBodyText = bodyText
          const prevText = bodyText
          bodyText = planer.extractFrom(bodyText, 'text/plain', window.document)
          if (prevText !== bodyText) {
            newState = {
              ...newState,
              hasQuoted: true,
            }
          }
          fullBodyElem = <pre className={styles.plainBody}>{fullBodyText}</pre>
        }
        bodyElem = <pre className={styles.plainBody}>{bodyText}</pre>
        newState = {
          ...newState,
          bodyElem,
          fullBodyElem,
          isBlockedRes: false,
        }
      } else if (type === 'text/calendar') {
        this.applyBodyElem(body, inlineImages)

        newState = {
          ...newState,
          isBlockedRes: false,
        }
      }

      this.loading = false
    }

    if (Object.keys(newState).length > 0) {
      this.setState(newState as EmailViewState)
    }
  }

  /**
   * OMD-131: 인용 전체 본문을 이 시점에 bake 한다 (applyBodyElem 에서 지연된 작업).
   *
   * 중복 실행 방지는 `fullBodyBakeStarted` 래치가 담당한다 — `state.fullBodyElem` 로 판정하면
   * bake 가 진행 중인(아직 setState 전) 동안 다시 눌러 2회 실행된다.
   *
   * 반환 promise 는 setState commit 까지 완료된 뒤 resolve 한다.
   * bake 결과 DOM 을 필요로 하는 소비자(인쇄 — `requestPrintPayload`)가 기다릴 수 있어야 한다.
   */
  bakeFullBody = (): Promise<void> => {
    const fullBodyHTML = this.pendingFullBodyHTML
    if (fullBodyHTML == null) return Promise.resolve()
    if (this.fullBodyBakeStarted === true) return this.fullBodyBakePromise ?? Promise.resolve()
    this.fullBodyBakeStarted = true

    const pseudoScopeId = this.props.threadItem?.mailInfo?.id

    // getSafeElement 는 sanitize/inline bake 완료된 본문 DOM(fragment)을 반환한다. setDivBody 의
    // ref 콜백이 fullBodyFragment 를 appendChild 로 삽입한다(HTML 문자열 재파싱 생략).
    const bake = getSafeElement(
      fullBodyHTML,
      {
        pseudoScopeId,
        signal: this.bakeController.signal,
        // 에디터 탭 활성화 중 이 변환이 메인 스레드를 경합하지 않도록 일시정지 게이트를 전달한다.
        pauseGate: bakePauseGate,
        processElement: this.bodyBakeProcessElement,
        // OMD-243: 뷰어 settings(domainsForIFrame) 기반 본문 iframe 정책. web(미주입)은 undefined.
        mailBodyIframeSanitizer: makeMailBodyIframeSanitizer(IFace.getSettings()),
      },
      this.getBodyWidthArg(),
    )
      .then((fragment) => {
        if (this.isUnmounted) return
        // 완성된 전체 본문 DOM. setDivBody 의 ref 콜백에서 appendChild 로 삽입한다.
        this.fullBodyFragment = fragment
        const fullBodyElem = (
          <div
            className={classNames(styles.htmlMailBody, 'htmlMailBody')}
            ref={(ref) => {
              this.setDivBody(ref, true)
            }}
          />
        )

        return new Promise<void>((resolve) => {
          this.setState({ fullBodyElem }, () => resolve())
        })
      })
      // 중단(다른 메일로 이동)은 무시하고, 그 외 실제 에러는 표면화한다(조용한 실패 방지).
      .catch((err) => {
        if ((err as { name?: string })?.name !== 'AbortError') throw err
      })

    this.fullBodyBakePromise = bake
    return bake
  }

  showQuoted = (e?: React.MouseEvent) => {
    this.isQuotedVisible = true

    // OMD-131: 인용 본문 bake 는 여기서 시작한다(applyBodyElem 은 원본만 보관). setState 보다
    // 먼저 호출해도 `body` 폴백(fullBodyElem || bodyElem)이 인용 제거 본문을 계속 보여준다.
    this.bakeFullBody()

    this.setState({
      isQuotedVisible: this.isQuotedVisible,
    })

    const { threadItem } = this.props
    const { mailInfo } = threadItem
    if (this.divFrameMap.fullBody != null) {
      ;(window as WindowWithDocUtil).docUtil.addViewElement(
        mailInfo.id,
        bodyContentRoot(this.divFrameMap.fullBody),
      )
    } else if (this.divFrameMap.body != null) {
      ;(window as WindowWithDocUtil).docUtil.addViewElement(
        mailInfo.id,
        bodyContentRoot(this.divFrameMap.body),
      )
    }

    if (e != null) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  onSetInlineImages = (inlineImages: InlineImage[]) => {
    if (inlineImages != null && Array.isArray(inlineImages) && inlineImages.length > 0) {
      this.inlineImgList = [...this.inlineImgList, ...inlineImages]

      inlineImages.forEach((inlineData) => {
        const { cid, url } = inlineData
        this.existInlineImgMap[cid] = inlineData
        Object.values(this.divFrameMap).forEach((divElem) => {
          this.applyInlineImg(bodyContentRoot(divElem), cid, url)
        })
      })
    }
  }

  onSetSecureSendInfo = (data: {
    isSecureMail: boolean
    expiredDate: string | null
    secureMailId: string | null
  }): void => {
    this.setState({
      isSecureMail: data.isSecureMail,
      secureSendExpired: data.expiredDate,
      secureMailId: data.secureMailId,
    })

    if (this.secureSendExpireTimerId != null) {
      clearTimeout(this.secureSendExpireTimerId)
      this.secureSendExpireTimerId = null
    }

    if (data.isSecureMail && data.expiredDate != null) {
      this.scheduleSecureSendExpireCheck(data.expiredDate)
    }
  }

  onSetReadReceiptInfo = (isReadReceipt: boolean, data: ReadReceiptInfo | null): void => {
    this.setState({
      isReadReceipt,
      readReceiptStatus: data != null ? data.status : null,
      receiptId: data != null ? data.receiptId : null,
    })
  }

  openReadReceiptDetail = (): void => {
    if (this.state.readReceiptStatus == null) return
    // 5차 보완: viewer iframe 마스크 클립 회피를 위해 host 컨텍스트에서 popup 마운트.
    // host 가 emailId 로 emailInfoMap.readReceiptInfo.status 를 lookup 후 popup 을 띄움.
    const emailId = this.props.threadItem?.mailInfo?.id
    if (emailId == null || emailId.length === 0) return
    // 칩 클릭 시 refresh 도 함께 트리거 — host 가 캐시 삭제 후 applyReadReceiptInfo 재호출.
    // host 의 dispatchReadReceiptInfoToViewer 가 readReceiptListenerStore.notify 를 부르며,
    // 동시에 띄워진 popup 의 Content 가 동일 emailId 를 구독하므로 자동 갱신된다.
    IFace.WebToNative.requestRefreshReadReceiptInfo?.(emailId)
    IFace.WebToNative.openReadReceiptDetailPopup(emailId)
  }

  refreshReadReceipt = (emailId: string): void => {
    if (emailId == null || emailId.length === 0) return
    // ViewerIFace WebToNative 채널 — 호스트(apps/owma) 측에서 캐시 삭제 후 applyReadReceiptInfo 재호출
    IFace.WebToNative.requestRefreshReadReceiptInfo?.(emailId)
  }

  scheduleSecureSendExpireCheck = (expiredDate: string): void => {
    const THIRTY_MINUTES = 30 * 60 * 1000
    const expireTime = new Date(expiredDate).getTime()
    const remaining = expireTime - Date.now()

    if (remaining <= 0) {
      this.forceUpdate()
      return
    }

    const delay = remaining > THIRTY_MINUTES ? THIRTY_MINUTES : remaining

    this.secureSendExpireTimerId = setTimeout(() => {
      this.secureSendExpireTimerId = null
      if (this.isUnmounted) return

      if (Date.now() >= expireTime) {
        this.forceUpdate()
      } else {
        this.scheduleSecureSendExpireCheck(expiredDate)
      }
    }, delay)
  }

  notifyDownloadProgress = (attachmentId: string, progress: any) => {
    const onProgressCB = this.attachmentProgressMap[attachmentId]
    if (onProgressCB != null) {
      onProgressCB(null, progress)
    }
  }

  applyInlineImgList = (divElem: HTMLElement) => {
    this.inlineImgList.forEach((inlineData) => {
      const { cid, url } = inlineData
      this.existInlineImgMap[cid] = inlineData
      this.applyInlineImg(divElem, cid, url)
    })
  }

  // OMD-28: getSafeElement 에 넘길 본문 content box 폭(px 문자열). 호출 시점에 항상 마운트되어 있는
  // `.container`(containerRef) 기준으로 계산한다. 폭이 유효하지 않으면 undefined 를 반환해 getSafeElement
  // 이 기본값(720px)으로 폴백하도록 한다.
  getBodyWidthArg = (): string | undefined => {
    const container = this.containerRef
    const contentWidth =
      container != null ? container.clientWidth - VIEW_CONTAINER_HORIZONTAL_PADDING : 0
    const widthArg = contentWidth > 0 ? `${contentWidth}px` : undefined

    // 폭이 0/미마운트라 720 으로 조용히 폴백되면 diff 검증(720==720)이 이를 못 잡으므로, dev 에서만
    // 폴백/전달 폭을 남겨 폴백 발생을 가시화한다(프로덕션 번들에서는 dead code 로 제거).
    if (!__ENV_PRODUCTION__) {
      if (widthArg == null) {
        console.warn('[OMD-28] getSafeElement width fallback → 720px', {
          hasContainer: container != null,
          clientWidth: container?.clientWidth ?? null,
        })
      } else {
        console.debug('[OMD-28] getSafeElement width', widthArg)
      }
    }

    return widthArg
  }

  // OMD-29: 리사이즈로 본문 content 폭이 원본 @media breakpoint 구간을 교차하면, 보관한 원본으로
  // applyBodyElem 을 재호출해 새 폭 기준으로 getSafeElement 을 다시 굽는다(B안). 같은 구간 내 변화는
  // @media 결과가 동일하므로 스킵한다. 비반응형·대형 메일(크기 가드)·원본 미보관 시엔 아무것도 하지 않는다.
  // 폭 기준은 초기 렌더(getBodyWidthArg)와 동일한 `clientWidth − 88` 로 통일한다.
  checkMediaQueryRecalc = () => {
    const body = this.originalBody
    if (body == null || this.mediaBreakpoints.length === 0) return
    if (!isRecalcAllowed(this.bodyElementCount, this.lastBakeMs)) return

    const container = this.containerRef
    if (container == null) return
    const contentWidth = container.clientWidth - VIEW_CONTAINER_HORIZONTAL_PADDING
    if (contentWidth <= 0) return

    const prevWidth = this.prevZoneWidth
    this.prevZoneWidth = contentWidth
    // 최초 관측(이전 폭 없음)은 기준만 세우고 재계산하지 않는다.
    if (prevWidth == null) return

    if (crossesZone(prevWidth, contentWidth, this.mediaBreakpoints)) {
      this.applyBodyElem(body, this.originalInlineImages)
    }
  }

  applyBodyElem = (body: string, inlineImages?: InlineImage[]) => {
    // 이미 다른 메일로 이동(언마운트)했으면 뒤늦게 도착한 본문의 파싱·변환을 시작하지 않는다(freeze 방지).
    if (this.isUnmounted === true) return

    // 재진입(리사이즈 breakpoint 재계산, 본문 갱신 등) 시 진행 중인 이전 bake(body·fullBody)를
    // 중단하고 컨트롤러를 교체한다(마지막 호출 승리). 중단된 변환은 다음 yield 경계에서 CPU 를
    // 회수하고, aborted promise 는 resolve 하지 않으므로 이전 폭/내용의 결과가 새 결과를 덮어쓸
    // 수 없다. 인용 펼침 상태의 fullBody bake 중단은 무해 — 그 상태의 재진입은 main bake 가 인용
    // 포함 전체 본문을 굽고, 표시도 `fullBodyElem || bodyElem` 폴백이라 새 bodyElem 이 커버한다.
    this.bakeController.abort()
    this.bakeController = new AbortController()

    const { isFirst } = this.props
    // OMD-29: 재계산 입력(원본) 보관. 리사이즈로 breakpoint 구간을 교차할 때 이 원본으로 applyBodyElem
    // 을 재호출해 새 폭 기준 getSafeElement 을 다시 굽는다. 비반응형이면 아래 onMediaConditions 에서 해제.
    this.originalBody = body
    this.originalInlineImages = inlineImages
    // OMD-28: 실제 컨테이너 폭을 getSafeElement width 로 넘겨 media query/%/auto 레이아웃이 실제 표시
    // 폭 기준으로 인라인되도록 한다. fullBody/body 두 호출 모두 같은 `.viewContainer` 안에 렌더되므로
    // 동일 폭을 적용한다.
    const bodyWidth = this.getBodyWidthArg()
    const inlineImageMap: Record<string, InlineImage> = {}
    if (inlineImages != null) {
      inlineImages.forEach((imageFile) => {
        inlineImageMap[imageFile.cid] = imageFile
      })
    }

    let bodyHTML = body
    let fullBodyHTML: string | null = null
    // OFM-221: planer 가 실행되면 bodyHTML 이 문자열 단계에서 별도 변형되어 doc 와 불일치하므로,
    // planer 미실행 시에만 파싱된 doc 를 getSafeElement 에 직접 전달해 재파싱을 생략한다.
    let plannerRan = false

    let hasExternalImg = false

    const domParser = new DOMParser()
    const doc = domParser.parseFromString(bodyHTML, 'text/html')

    // draft-body-style-from-viewer: 표시용으로 이미 파싱한 이 시점(이미지 src 치환/인용 제거
    // mutate 전)에 body 스타일 골격만 추출해 host 에 보고한다 — draft 편집 열기의 defaultStyle
    // 시딩이 본문 전체 재파싱(2.7MB WebKit ~4s) 없이 재사용한다. 순수 additive(실패 무해).
    // (골격은 수백 B 로 싸고 reopen 시딩 전용이라 draft 게이트를 유지 — 아래 doc 캡처와 별개.)
    if (this.props.threadItem?.mailInfo?.draft === true) {
      try {
        const bodyIdx = body.indexOf('<body')
        const bodyEnd = bodyIdx >= 0 ? body.indexOf('>', bodyIdx) : -1
        // prefix(원문 선두~body 열림 태그 끝)는 stale 검증용. 비정상적으로 길면 보고를 생략해
        // host 가 기존 전체 파싱 경로로 폴백하게 한다.
        if (bodyEnd >= 0 && bodyEnd < 65536) {
          IFace.WebToNative.reportDraftBodyStyle?.(
            this.props.threadItem.mailInfo.id,
            buildDraftBodyStyleSkeleton(doc),
            body.length,
            body.slice(0, bodyEnd + 1),
          )
        }
      } catch {
        /* additive — 보고 실패는 기존 경로(전체 파싱)로 폴백될 뿐 */
      }
    }
    // parsed-body-doc-from-viewer: 같은 파싱 산출물의 원본 보존 사본(deep clone)을 host 에
    // 전달한다 — 편집(draft)·reply/forward·resend 열기의 본문 재파싱(~4s) 제거. clone 은
    // 아래 mutate(이미지 src 치환 등) 전에 떠야 한다.
    //   - Gate B(정책): host 가 mailInfo 로 선언한 enable_capture_element === true 인 메일만.
    //     미전송 host(EML viewer·독립 뷰어 앱·플래그 미이식 platform)는 자동 비활성(fail-safe).
    //   - 크기 실익: 파싱 비용은 본문 크기에 비례(~1.5ms/KB, WebKit 실측 외삽)하므로
    //     CAPTURE_MIN_BODY_LEN 미만은 캡처 이득이 미미해 제외(캐시 FIFO 보호 겸용).
    //   - Gate A(전송 능력): ViewerIFace 가 same-realm 직접 핸들러 존재를 확인한 뒤에만
    //     makeDoc(clone) 을 호출하므로 미지원 환경에선 clone 비용이 없다.
    if (
      this.props.threadItem?.mailInfo?.enable_capture_element === true &&
      body.length >= CAPTURE_MIN_BODY_LEN
    ) {
      try {
        IFace.WebToNative.reportParsedBodyDocument?.(
          this.props.threadItem.mailInfo.id,
          () => doc.cloneNode(true) as Document,
          body,
        )
      } catch {
        /* additive — 전달 실패는 기존 경로(전체 파싱)로 폴백될 뿐 */
      }
    }

    // OMD-29: 크기 가드용 요소 수(첫 bake 전 측정). 임계 이상이면 리사이즈 재계산을 비활성한다.
    this.bodyElementCount = doc.querySelectorAll('*').length

    const imgList = doc.querySelectorAll('img')

    imgList.forEach((img) => {
      if (img.src.toLowerCase().indexOf('resources:') !== 0) {
        if (img.src.toLowerCase().indexOf('cid:') === 0) {
          const cid = img.src.substring(4)
          const inlineData = inlineImageMap[cid]
          if (inlineData != null) {
            img.src = inlineData.url
          } else {
            img.setAttribute('owa-cid', img.src)
            img.src = ''
          }
        } else if (
          this.isBlockedRes !== false &&
          REMOTE_IMG_SRC.test(img.getAttribute('src') ?? '') &&
          // OMD-191: 수신확인 추적 픽셀 등 예외 URL 은 차단하지 않는다. 예외(false)일 때만
          // 차단을 건너뛰고, undefined(브릿지 미초기화)/true 는 안전하게 차단한다.
          IFace.WebToNative?.isExternalImageSrc?.(img.src) !== false
        ) {
          // 원격(http/https/protocol-relative) 이미지만 차단·배너 대상. blob:/data: 인라인 이미지는
          // 네트워크 fetch 가 없어 무조건 표시한다 — 예전엔 여기서 모든 비-cid 이미지를 owa-src 로
          // 옮겨(blob 포함) ①인라인만 있는 메일에도 차단 배너가 뜨고 ②getSafeHTML 이 blob owa-src 를
          // 스킴 검증(http 아님)으로 제거해 applyBlockedImg 복원 불가 → 버튼을 눌러도 재마운트 전엔
          // 이미지가 안 보였다. raw src 속성으로 판별(img.src 프로퍼티는 상대경로를 절대화해 shim 의
          // REMOTE_IMG_SRC 계약과 어긋날 수 있다).
          hasExternalImg = true
          img.setAttribute('owa-src', img.getAttribute('src') ?? '')
          const srcset = img.getAttribute('srcset')
          img.src = ''
          if (srcset != null) {
            img.setAttribute('owa-srcset', srcset)
            img.setAttribute('srcset', '')
          }
        } else {
          // 외부 이미지 즉시 표시 경로(차단 해제 또는 예외 URL): mixed content 인 http 이미지를
          // https 로 승격해 Firefox 의 CSP(img-src) 차단을 피한다.
          img.src = upgradeInsecureUrl(img.src)
          const srcset = img.getAttribute('srcset')
          if (srcset != null) {
            img.setAttribute('srcset', upgradeInsecureSrcset(srcset))
          }
        }
      }
    })

    if (this.isQuotedVisible === false && isFirst !== true) {
      fullBodyHTML = doc.documentElement.outerHTML

      const quotedHeader = doc.querySelector(
        '[id^="quoted_header_"].quoted_header_editor, div[style="border:none;border-top:solid #B5C4DF 1.0pt;padding:3.0pt 0cm 0cm 0cm"]',
      )
      if (quotedHeader != null) {
        let tmpElem: Element | null = quotedHeader
        while (tmpElem != null) {
          const next =
            tmpElem.nextSibling != null ? tmpElem.nextSibling : tmpElem.parentElement?.nextSibling
          tmpElem.parentElement?.removeChild(tmpElem)
          tmpElem = next as Element | null
        }

        this.setState({
          hasQuoted: true,
        })
      }
      bodyHTML = doc.documentElement.outerHTML

      bodyHTML = bodyHTML.replace('\r\n', '\n')
      // quotedHeader 시그니처가 이미 매칭되어 인용이 제거된 경우 planer 호출 스킵.
      // 큰 메일일수록 planer 비용이 크므로 중복 실행을 피한다.
      if (quotedHeader != null) {
      } else {
        plannerRan = true
        const prevHTML = bodyHTML
        bodyHTML = planer.extractFrom(bodyHTML, 'text/html', window.document)

        if (prevHTML !== bodyHTML) {
          this.setState({
            hasQuoted: true,
          })
        }
      }
    } else {
      bodyHTML = doc.documentElement.outerHTML
    }

    if (hasExternalImg === false) {
      this.setState({
        hasExternalImg: false,
      })
    } else {
      this.setResourceBlocked(this.isBlockedRes!)
    }

    // 툴바 인용구(variant) 의사요소 장식을 뷰어에 보존하기 위한 메일별 scope id.
    // getSafeElement 은 이 값이 있으면 의사요소 <style data-omw-pseudo> 를 unscoped 로 주입하고,
    // setDivBody 가 `@scope ([data-email-id="{id}"] .htmlMailBody)` 로 감싸 메일 본문에 격리한다.
    const pseudoScopeId = this.props.threadItem?.mailInfo?.id

    // OMD-131: 인용 전체 본문(fullBody)은 **bake 하지 않고 원본만 보관**한다. 접힘 상태의
    // fullBodyElem 은 `.expandTextBtn + .htmlMailBody { display: none }`(style.module.scss)로
    // 숨겨져 있어 `인용 보기` 를 누르기 전까지 화면에 나타나지 않는데, 여기서 미리 구우면
    // **표시되는 본문보다 큰 HTML** 을 getSafeElement(DOMParser + 전체 순회)로
    // 한 번 더 돌리고 그 DOM 을 mount(setDivBody → applyPseudoStyleScope·linkifyMailNode 전체
    // 순회)까지 한다. 두 bake 가 겹치면 메인 스레드 정지 시간이 그대로 합산된다.
    //
    // → bake 를 `showQuoted`(사용자가 실제로 인용을 펼치는 시점)로 옮긴다. bake 완료 전에는
    // `body = isQuotedVisible === false ? bodyElem : fullBodyElem || bodyElem`(uiExt) 폴백이
    // 인용 제거 본문을 계속 보여주므로 화면이 비지 않는다.
    if (this.isQuotedVisible === false && fullBodyHTML != null) {
      this.pendingFullBodyHTML = fullBodyHTML
      this.fullBodyBakeStarted = false
      // 리사이즈 재계산 등으로 재진입 시 이전 bake 결과(fragment·fullBodyElem)를 비워 stale 표시를 막는다.
      this.fullBodyFragment = undefined
      this.setState({ fullBodyElem: null })
    } else {
      this.pendingFullBodyHTML = undefined
      // 인용 펼침 상태(isQuotedVisible)의 재진입: 진입부 abort 로 in-flight fullBody bake 가
      // 사라질 수 있으므로 래치/프로미스/fragment 를 함께 정리한다. 이 분기의 main bake 는 인용
      // 포함 전체 본문을 새 폭으로 굽고, 완료 시 fullBodyElem 도 그 결과로 수렴시킨다(아래 .then)
      // — 수렴 없이는 스피너(uiExt: isQuotedVisible && fullBodyElem == null)가 영구 표시되고,
      // 완료돼 있던 이전 폭 fullBodyElem 이 새 본문을 가린다.
      this.fullBodyBakeStarted = false
      this.fullBodyBakePromise = null
      this.fullBodyFragment = undefined
    }

    // OFM-221: planer 미실행 시 bodyHTML === doc.outerHTML 이므로, 재직렬화/재파싱 대신 doc 를 직접
    // 전달해 getSafeElement 내부 DOMParser 를 생략한다. planer 실행 시엔 문자열이 변형되었으므로 폴백.
    const bakeStart = performance.now()
    getSafeElement(
      plannerRan ? bodyHTML : doc,
      {
        pseudoScopeId,
        signal: this.bakeController.signal,
        // 에디터 탭 활성화 중 이 변환이 메인 스레드를 경합하지 않도록 일시정지 게이트를 전달한다.
        pauseGate: bakePauseGate,
        // OMD-243: 뷰어 settings(domainsForIFrame) 기반 본문 iframe 정책. web(미주입)은 undefined.
        mailBodyIframeSanitizer: makeMailBodyIframeSanitizer(IFace.getSettings()),
        // OMD-29: bake 시점 CSSOM 의 @media condition 으로 width breakpoint 를 파싱해 보관한다.
        // getSafeElement executor 는 동기 실행이라 아래 .then 이전에 mediaBreakpoints 가 채워진다.
        // 비반응형(경계 없음)이면 재계산이 불필요하므로 원본 보관을 해제해 메모리를 아낀다.
        onMediaConditions: (conditionTexts, remSize) => {
          this.mediaBreakpoints = parseWidthBreakpoints(conditionTexts, remSize)
          if (this.mediaBreakpoints.length === 0) {
            this.originalBody = undefined
            this.originalInlineImages = undefined
          }
        },
        processElement: this.bodyBakeProcessElement,
      },
      bodyWidth,
    )
      .then((fragment) => {
        if (this.isUnmounted === true) return
        // OMD-29: 첫 bake 소요를 크기 가드 보조 기준으로 기록(임계 초과면 리사이즈 재계산 비활성).
        const bakeEnd = performance.now()
        this.lastBakeMs = bakeEnd - bakeStart
        // getSafeElement 가 반환한 본문 DOM. ref 콜백(setDivBody)에서 appendChild 로 삽입한다.
        this.bodyFragment = fragment
        const bodyElem = (
          <div
            className={classNames(styles.htmlMailBody, 'htmlMailBody')}
            ref={(ref) => {
              this.setDivBody(ref, false)
            }}
          />
        )

        this.loading = false

        this.measureBodyRender('element', bakeStart, bakeEnd, body.length)
        this.setState({
          bodyElem,
          // 인용 펼침 상태의 bake 는 인용 포함 전체 본문이므로(위 isQuotedVisible 분기에서
          // pendingFullBodyHTML 해제) fullBody 표시도 이 결과로 수렴시킨다 — 재진입 abort 로
          // fullBody bake 가 사라져도 스피너가 영구 표시되지 않고, 이전 폭 fullBodyElem 이
          // 새 본문을 가리지 않는다. hasExpandBtn(접힘) 상태가 아니므로 중복 렌더는 없다.
          ...(this.isQuotedVisible === true && this.pendingFullBodyHTML == null
            ? { fullBodyElem: bodyElem }
            : null),
        })
      })
      // 중단(다른 메일로 이동)은 무시하고, 그 외 실제 에러는 표면화한다(조용한 실패 방지).
      .catch((err) => {
        if ((err as { name?: string })?.name !== 'AbortError') throw err
      })
  }

  // 인용 전체 본문(fullBody) 변환은 body/fullBody 공통 규칙을 쓴다. page 분기 제거 + img eager 로딩.
  bodyBakeProcessElement = (element: HTMLElement) => {
    if (
      (element.style as StyleWithPage).page != null &&
      (element.style as StyleWithPage).page !== ''
    ) {
      ;(element.style as StyleWithPage).page = ''
    }
    // loading이 auto/lazy면 브라우저 장시간 실행·메모리 부족 시 이미지 로딩이 지연될 수 있음.
    if (element.tagName === 'IMG') {
      element.setAttribute('loading', 'eager')
    }
  }

  // 계측: bake 완료 기준 commit·paint 를 측정해 한 건으로 기록(기본 비활성).
  measureBodyRender = (
    mode: MailPerfMode,
    bakeStart: number,
    bakeEnd: number,
    bodyBytes: number,
  ) => {
    if (!isMailPerfEnabled()) return
    this.perfBodyCommitAt = undefined
    schedulePaintMeasure(
      {
        messageId: this.props.threadItem?.mailInfo?.id ?? '',
        mode,
        fetchMs: this.perfFetchMs,
        transformMs: bakeEnd - bakeStart,
        bodyBytes,
        elementCount: this.bodyElementCount,
      },
      bakeEnd,
      () => this.perfBodyCommitAt,
    )
  }

  initRef = (ref: HTMLElement | null) => {
    if (this.observerObj == null) {
      this.observerObj = {} as ObserverObj

      this.observerObj.onClick = (e: MouseEvent) => {
        console.log(' - onClick Body : ', e)
        const linkElem = bodyEventTarget(e).closest(`.${styles.htmlMailBody} a`)
        if (linkElem != null) {
          const url = (linkElem as HTMLAnchorElement).href

          const { threadItem } = this.props
          const { mailInfo } = threadItem

          IFace.WebToNative.openURL(mailInfo.id, url)

          e.preventDefault()
          e.stopPropagation()
          return
        }
      }

      this.observerObj.onDblClick = (e: MouseEvent) => {
        console.log(' - onDblClick Body : ', e)

        const imgElem = bodyEventTarget(e).closest(`.${styles.htmlMailBody} img`)
        if (imgElem != null) {
          const aTag = imgElem.closest(`.${styles.htmlMailBody} a`)
          if (aTag == null) {
            // a tag에 포함되지 않은 img일 때.
            const { src } = imgElem as HTMLImageElement
            if (src != null && src.toLowerCase().indexOf('resources://images') === 0) {
              console.log('img src : ', src)
              IFace.WebToNative.showInlineImage(src)
            } else {
              const cidAttr = imgElem.getAttribute('owa-cid')
              if (cidAttr != null && cidAttr.indexOf('cid:') === 0) {
                const cid = cidAttr.substring(4)
                const cidInfo = this.inlineImgList.find((item) => item.cid === cid)
                if (cidInfo != null) {
                  console.log('img src : ', cidInfo.url)
                  IFace.WebToNative.showInlineImage(cidInfo.url)
                }
              }
            }
          }
        }
      }

      this.observerObj.clear = () => {
        const { element } = this.observerObj!
        if (element != null) {
          this.observerObj!.element!.removeEventListener('click', this.observerObj!.onClick!)
          this.observerObj!.element!.removeEventListener('dblclick', this.observerObj!.onDblClick!)
        }
      }
    }

    if (ref != null && this.observerObj.ref != ref) {
      this.observerObj.clear()

      this.observerObj.ref = ref
      this.observerObj.element = ref

      if (this.observerObj.element != null) {
        this.observerObj.element.addEventListener('click', this.observerObj.onClick!)
        this.observerObj.element.addEventListener('dblclick', this.observerObj.onDblClick!)
      }
    }
  }

  setDivBody = (ref: HTMLElement | null, isFullBody: boolean) => {
    if (ref != null) {
      // 재마운트(예: 인용 펼치기로 fullBody 본문 div 위치가 B→A 로 이동) 시 이전 div 의 본문 DOM 을
      // 새 div 로 옮기기 위해, divFrameMap 갱신 전 이전 ref 를 보관한다.
      const prevRef = isFullBody ? this.divFrameMap.fullBody : this.divFrameMap.body

      if (isFullBody === true) {
        this.divFrameMap.fullBody = ref
      } else {
        this.divFrameMap.body = ref
      }

      // getSafeElement 가 반환한 본문 DOM 을 ref 에 삽입한다(HTML 문자열 재파싱 생략).
      // - 최초 bake/리사이즈 재계산: fragment 를 이동 삽입하고 소비(undefined)한다.
      // - 재마운트(fragment 이미 소비, React 가 본문 div 를 다른 위치로 이동): 이전 div 에 살아있는
      //   본문 DOM 을 새 div 로 옮긴다. innerHTML 방식과 달리 imperative DOM 은 React 재조정으로 자동
      //   복원되지 않으므로, 인용 펼치기(fullBody 위치 이동) 시 본문 유실을 막는다.
      // 본문을 넣을 요소 — light 면 ref, shadow 면 ref 의 shadow root 안 wrapper(bodyRoot.ts).
      const body = mountBodyRoot(ref, IFace.getSettings().bodyIsolation)
      const fragment = isFullBody ? this.fullBodyFragment : this.bodyFragment
      if (fragment != null) {
        body.replaceChildren()
        // F12: 부분 zoom 대상은 첫 layout 전에 감싸 둔다 — layout 된 대형 subtree 를 나중에 옮기지 않게.
        // 이 메일의 MailView 가 normal mode(원본 보기 뒤 재bake 등)면 감싸지 않는다 — 아무도 풀지 않는다.
        const zoomDocUtil = (window as WindowWithDocUtil).docUtil
        const zoomView = zoomDocUtil?.mailViewMap[this.props.threadItem.mailInfo.id]
        const willZoom =
          zoomView == null
            ? (window as { __devDisableOptimize?: boolean }).__devDisableOptimize !== true
            : zoomView.partialZoomMode === true
        if (willZoom)
          prewrapOverflowChildren(fragment, ref.clientWidth, zoomDocUtil?.expandIconElem ?? null)
        body.appendChild(fragment)
        // macOS 27 WebKit: temp bake iframe 제거로 끊긴 외부 이미지 로드를 뷰어 문서에서 재시작한다.
        // WebKit 전용 결함이므로 해당 엔진에서만 건다. applyBlockedImg/applyInlineImgList(아래) 의
        // fresh src 지정보다 반드시 앞서 호출해야 한다.
        if (IS_WEBKIT_ENGINE) restartBrokenImageLoads(body)
        if (isFullBody === true) {
          this.fullBodyFragment = undefined
        } else {
          this.bodyFragment = undefined
        }
      } else if (prevRef != null && prevRef !== ref) {
        const prevBody = bodyContentRoot(prevRef)
        if (prevBody.childNodes.length > 0) body.replaceChildren(...Array.from(prevBody.childNodes))
      }

      // 계측: 본문 div 에 DOM 삽입까지 완료된 시각(commit). appendChild 비용을 포함한다.
      if (isFullBody !== true && isMailPerfEnabled()) {
        this.perfBodyCommitAt = performance.now()
      }

      if (this.isBlockedRes === false) {
        this.applyBlockedImg(body)
      }

      this.applyInlineImgList(body)
      // OMD-160: 본문 복사 시 인라인 이미지 유지를 위한 리스너 등록(중복 방지 가드 내장).
      this.bindBodyClipboard(body)

      // 의사요소 보존 <style>(getSafeElement 이 unscoped 로 주입) 을 이 메일 본문으로만 한정.
      // @scope([data-email-id="{id}"] .htmlMailBody) 로 감싸 스레드 내 타 메일/앱 전역 오염을 막는다.
      // shadow 면 shadow root 자체가 이 메일 본문 경계라 wrapper class 만으로 scope 한다.
      const emailId = this.props.threadItem?.mailInfo?.id
      if (body !== ref) {
        applyPseudoStyleScope(body, '.htmlMailBody')
      } else if (
        emailId != null &&
        typeof CSS !== 'undefined' &&
        typeof CSS.escape === 'function'
      ) {
        applyPseudoStyleScope(ref, `[data-email-id="${CSS.escape(emailId)}"] .htmlMailBody`)
      }

      const docUtil = (window as WindowWithDocUtil).docUtil
      if (docUtil != null) {
        const { threadItem } = this.props
        const { mailInfo } = threadItem
        if (this.isQuotedVisible === true && this.divFrameMap.fullBody != null) {
          docUtil.addViewElement(mailInfo.id, bodyContentRoot(this.divFrameMap.fullBody))
        } else if (this.isQuotedVisible === false && this.divFrameMap.body != null) {
          docUtil.addViewElement(mailInfo.id, bodyContentRoot(this.divFrameMap.body))
        }
        // OMD-29: 리사이즈 재계산으로 본문 DOM 이 교체되면 다크모드 색이 초기화된다. addViewElement
        // 경로(setContainer)는 zoom/observer 만 재설정하고 다크모드는 건드리지 않으므로, 활성 시 재적용.
        if (docUtil.isDarkMode() === true) {
          docUtil.applyDarkMode()
        }
      }

      linkifyMailNode(body)
    }
  }

  scrollIntoView = () => {
    if (this.containerRef != null) {
      console.log(' --- fold : ', this.props.fold, this.state.isFold)
      const { owner } = this.props
      owner.setScrollVisible(true)
      if (this.state.isFold !== false) {
        setTimeout(() => {
          scrollIntoViewIfNeeded(this.containerRef!.parentElement)
          owner.setScrollVisible(false)
        }, 250)
      } else {
        scrollIntoViewIfNeeded(this.containerRef!.parentElement)
        owner.setScrollVisible(false)
      }
    }
  }

  applyInlineImg = (divElem: HTMLElement, cid: string, url: string) => {
    if (divElem != null) {
      const imgs = divElem.querySelectorAll(`img[owa-cid="cid:${cid}"]`)
      imgs.forEach((img) => {
        ;(img as HTMLImageElement).src = url
      })
    }
  }

  // host(desktop)가 네이티브 인라인 변환기(officemail-inline://)를 주입했는지. trustConvertible 게이트
  // 완화와 마운트 즉시 예열의 공통 판정(OMD-193). web(미주입)은 false → 기존 동작 유지(무회귀).
  hasNativeInlineConverter = (): boolean =>
    (IFace.WebToNative.hasNativeInlineImageConverter as (() => boolean) | undefined)?.() === true

  // OMD-160/§15.14-①②: 본문 컨테이너에 selectstart 를 붙여 인라인 이미지 선변환을 예열한다.
  // copy 처리는 DocUtil.processCopy 가 rootContainer 단일 리스너로 담당하므로(버블링), 여기서
  // copy 는 등록하지 않는다. 선변환 결과는 EditorMgr 공유 map 에 push 된다.
  bindBodyClipboard = (elem: HTMLElement) => {
    if (this.boundBodyElems.includes(elem)) return
    this.boundBodyElems.push(elem)
    elem.addEventListener('selectstart', this.onBodySelectStart)
    // OMD-193 레이스 방지: desktop(officemail-inline://) 선변환은 Rust 프로토콜 디스크 read 라
    // web 의 in-memory blob 보다 느려, 열자마자 빠른 선택+복사 시 selectstart→convert(async)가 못
    // 끝나 map miss 로 이미지가 소실된다(processCopy 폴백은 blob 전용이라 구제 못 함). 네이티브
    // 변환기 주입(desktop) 시엔 본문이 DOM 에 마운트되는 이 시점에 즉시 예열해 lead time 을 확보한다
    // (마운트→사용자 복사는 항상 수백 ms+ ≫ 디스크 read). web(blob 즉시)은 레이스가 없어 selectstart
    // lazy 를 유지한다(비복사 열람 변환 비용·메모리 절감, 무회귀).
    if (this.hasNativeInlineConverter()) {
      this.ensureInlineDataUrls()
    }
  }

  unbindBodyClipboard = () => {
    this.boundBodyElems.forEach((elem) => {
      elem.removeEventListener('selectstart', this.onBodySelectStart)
    })
    this.boundBodyElems = []
    // inlineDataUrlMap 은 EditorMgr(DocUtil) 소유 공유 map 이라 여기서 비우지 않는다(다른 본문 캐시
    // 보존). DocUtil 재생성 시에만 초기화된다. pending 은 이 뷰의 in-flight 요청이라 정리한다.
    this.inlineDataUrlPending.clear()
  }

  // 선택 시작 시(복사 의도 신호) 인라인 이미지 blob URL 을 data URL 로 미리 변환해 둔다. 복사 시점의
  // 동기 치환을 위해 사전 준비하며, 비복사 열람에는 변환 비용을 물리지 않는다.
  onBodySelectStart = () => {
    this.ensureInlineDataUrls()
  }

  ensureInlineDataUrls = () => {
    const docUtil = (window as WindowWithDocUtil).docUtil
    if (docUtil == null) return
    // 판별식(extract-common-web T0-5 + OMD-193 predicate 주입): 변환 대상 = (inlineImgList 일치
    // ∪ 마커 보유) ∩ isConvertible. 스킴 판별·변환은 WebToNative 브릿지로 위임 — host(desktop)가
    // NineNative 로 주입하면 그 구현(officemail-inline:// 등), 미주입(web)이면 blob:/XHR 기본.
    // applyInlineImg 로 늦게 해석된 cid 이미지는 변환 대상 src + owa-cid 마커를 갖지만
    // inlineImgList 에 없을 수 있어, 마커 기반 수집이 그 gap 을 메운다. 미추적·비마커 대상은
    // collectConvertibleImgSrcs 가 로깅 후 제외한다. (initIFace 이전 호출 시 web 기본 폴백.)
    const isConvertible =
      (IFace.WebToNative.isInlineImageConvertible as ((src: string) => boolean) | undefined) ??
      isBlobInlineImageSrc
    const convert =
      (IFace.WebToNative.convertInlineImageToDataUrl as
        | ((src: string) => Promise<string | null>)
        | undefined) ?? blobUrlToDataUrl
    // host(desktop)가 네이티브 인라인 변환기를 주입했으면 isConvertible 은 host 소유 커스텀 스킴
    // (officemail-inline://)만 통과시키는 정밀 predicate 라, 통과분을 trackedUrls·마커 게이트 없이
    // 신뢰한다. 수신 메일은 Rust 가 렌더 전 cid:→officemail-inline:// 치환을 끝내 추적·마커 어느
    // 쪽에도 안 잡히므로(가설③), 이 신뢰가 없으면 복사 시 인라인 이미지가 소실된다. web(미주입)은
    // false → generic blob: 엄격 게이트 유지(무회귀).
    const trustConvertible = this.hasNativeInlineConverter()
    const trackedUrls = new Set(
      this.inlineImgList
        .map(({ url }) => url)
        .filter((url): url is string => url != null && isConvertible(url)),
    )
    const candidates = new Set<string>(trackedUrls)
    const containers = [this.divFrameMap.body, this.divFrameMap.fullBody]
      .filter((elem): elem is HTMLElement => elem != null)
      .map(bodyContentRoot)
    containers.forEach((container) => {
      collectConvertibleImgSrcs(container, trackedUrls, isConvertible, trustConvertible).forEach(
        (src) => candidates.add(src),
      )
    })

    candidates.forEach((url) => {
      // 이미 공유 map(EditorMgr)에 있거나 변환 진행 중이면 skip. 완료분은 map 에 push(§15.14-②).
      if (docUtil.getInlineDataUrl(url) != null || this.inlineDataUrlPending.has(url)) return
      this.inlineDataUrlPending.add(url)
      void convert(url).then((dataUrl) => {
        this.inlineDataUrlPending.delete(url)
        if (this.isUnmounted || dataUrl == null) return
        docUtil.setInlineDataUrl(url, dataUrl)
      })
    })
  }

  applyBlockedImg = (divElem: HTMLElement) => {
    if (divElem != null) {
      const imgs = divElem.querySelectorAll(`img[owa-src]`)
      imgs.forEach((img) => {
        const htmlImg = img as HTMLImageElement
        // 차단 해제 복원 경로: http 이미지를 https 로 승격해 Firefox CSP 차단을 피한다.
        htmlImg.src = upgradeInsecureUrl(img.getAttribute('owa-src')!)
        const srcset = img.getAttribute('owa-srcset')
        if (srcset != null) {
          img.setAttribute('srcset', upgradeInsecureSrcset(srcset))
          img.removeAttribute('owa-srcset')
        }
        img.removeAttribute('owa-src')
      })
    }
  }

  editDraft = (e?: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    console.log(' --- editDraft : ', mailInfo.id)

    IFace.WebToNative.editDraft(mailInfo.id)

    if (e != null) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  deleteDraft = (e?: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    console.log(' --- deleteDraft : ', mailInfo.id)

    IFace.WebToNative.deleteDraft(mailInfo.id)

    if (e != null) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  downloadAll = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    console.log(' --- downloadAll : ', mailInfo.id)

    IFace.WebToNative.downloadAll(mailInfo.id)
  }

  setAttachmentRef = (ref: HTMLElement | null) => {
    if (ref != null) {
      if (this.attachmentsObserver == null) {
        this.attachmentsObserver = {} as AttachmentsObserver

        this.attachmentsObserver.calcHeight = () => {
          if (this.attachmentsObserver!.resizeTimer != null) {
            cancelAnimationFrame(this.attachmentsObserver!.resizeTimer)
          }
          this.attachmentsObserver!.resizeTimer = requestAnimationFrame(() => {
            if (
              this.attachmentsObserver!.element != null &&
              this.attachmentsObserver!.element.children.length > 0
            ) {
              const top = this.attachmentsObserver!.element!.offsetTop
              const lastTop = (this.attachmentsObserver!.element!.lastElementChild as HTMLElement)!
                .offsetTop
              if (top < lastTop) {
                let idx = 0
                let item = this.attachmentsObserver!.element!.children[idx] as HTMLElement
                while (
                  item.offsetTop === top &&
                  idx < this.attachmentsObserver!.element!.children.length - 1
                ) {
                  idx += 1
                  item = this.attachmentsObserver!.element!.children[idx] as HTMLElement
                }

                const count = idx

                console.log(' --- view count : ', count)

                this.setState({
                  attachmentMinCnt: count,
                })
              } else {
                this.setState({
                  attachmentMinCnt: this.attachmentsObserver!.element!.children.length,
                })
              }
              this.attachmentsObserver!.resizeTimer = undefined
            }
          }) as number
        }

        this.attachmentsObserver.observer = new ResizeObserver(() => {
          console.log(' resize attachment - ')
          this.attachmentsObserver!.calcHeight()
        })

        this.attachmentsObserver.clear = () => {
          const { observer, element, resizeTimer } = this.attachmentsObserver!
          if (observer != null && element != null) {
            observer.disconnect()
            if (resizeTimer != null) {
              cancelAnimationFrame(resizeTimer)
            }
          }
        }
      }

      if (this.attachmentsObserver.ref !== ref || this.attachmentsObserver.element !== ref) {
        this.attachmentsObserver.clear()

        this.attachmentsObserver.element = ref
        this.attachmentsObserver.ref = ref

        if (this.attachmentsObserver.element != null) {
          this.attachmentsObserver.calcHeight()
          this.attachmentsObserver.observer!.observe(this.attachmentsObserver.element, {
            box: 'border-box',
          })
        }
      }
    }
  }

  enableExpandBtn = (enable: boolean) => {
    if (enable !== this.isExpandEnabled) {
      this.isExpandEnabled = enable
      this.setState({
        isExpandEnabled: this.isExpandEnabled,
      })
    }
  }

  setViewNormal = () => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    ;(window as WindowWithDocUtil).docUtil.mailViewMap[mailInfo.id].checkOption({ mode: 'normal' })
    this.enableExpandBtn(false)
  }

  onClickICal = (_e: React.MouseEvent, action: string, resAction?: number) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const resActionValue = resAction != null ? resAction : -1

    console.log('onClick', action, resActionValue)
    let type = -1
    switch (action) {
      case 'accept':
        type = 0
        break
      case 'tentative':
        type = 1
        break
      case 'decline':
        type = 2
        break
      case 'remove-calendar':
        type = 3
        break
      case 'propose-review':
        type = 4
        break
      case 'propose-accept':
        type = 5
        break
      default:
        type = -1
    }

    if (type > -1) {
      IFace.WebToNative.responseMeeting(mailInfo.id, type, resActionValue)
    }
  }

  // OMD-194: host(NineNative.print → NativeToWeb.requestPrintPayload)가 인쇄 payload 를 요청.
  // 뷰어는 platform-specific 로직(window.open / DOM 조립 / print())을 모르고, 자기 state 로
  // payload 만 만들어 콜백으로 반환한다. fold 상태에선 본문 DOM 이 렌더되지 않으므로 펼친 뒤
  // 수집한다(기존 타이밍 보존). 실제 인쇄는 apps/owma/src/utils/ui/printEmail.ts.
  //
  // OMD-131: 인용을 펼치지 않은 채 인쇄하면 fullBody(인용 포함)가 아직 bake 되지 않았다.
  // 인쇄 payload(buildPrintPayload) 는 divFrameMap.fullBody || divFrameMap.body 를 쓰므로,
  // pendingFullBodyHTML 이 있는 경우(= 인용이 접혀 있음) bakeFullBody() 를 먼저 기다린다.
  requestPrintPayload = (returnCallback: (payload: PrintPayload | null) => void) => {
    const isFold = this.isFold()
    if (isFold === true) {
      this.setFold(false)
      setTimeout(() => {
        this.bakeFullBody().then(() => {
          returnCallback(this.buildPrintPayload())
        })
      }, 100)
      return
    }
    this.bakeFullBody().then(() => {
      returnCallback(this.buildPrintPayload())
    })
  }

  // 인쇄용 본문 HTML 을 얻는다. 다크모드에서는 본문 DOM 에 다크 색이 적용돼 있어 그대로 뽑으면
  // 인쇄물도 다크 색으로 나온다. 라이브 DOM 을 건드리지 않도록 content root 를 clone 한 뒤,
  // 공유 colorMgr 로 element 색을 원복하고 dark mode 가 삽입한 <style data-nine-style> 를 제거한다.
  // (shadow-dom 모드 대응: cloneNode 는 shadow root 를 복제하지 않으므로 bodyDiv 가 아니라
  //  실제 content root 인 bodyContentRoot(bodyDiv) 를 clone 해야 본문이 비지 않는다.)
  getPrintBodyHtml = (bodyDiv: HTMLElement): string => {
    const contentRoot = bodyContentRoot(bodyDiv)
    const docUtil = (window as WindowWithDocUtil).docUtil
    if (docUtil == null || docUtil.isDarkMode() !== true) {
      return contentRoot.innerHTML
    }
    const clone = contentRoot.cloneNode(true) as HTMLElement
    // ① element 인라인/속성 다크 색 원복(공유 colorMgr 의 color map 사용 → detached clone 에도 동작).
    docUtil.colorMgr._restoreElemColor(clone)
    // ② dark mode 가 원본 <style> 옆에 삽입한 다크 오버라이드 제거. _restoreStyleTagCss 는 라이브
    //    본문 shadow 가 공유하는 모듈 레벨 CSSStyleSheet 를 비우는 side-effect 가 있어 clone 에 쓰지 않는다.
    clone.querySelectorAll('[data-nine-style="true"]').forEach((el) => el.remove())
    return clone.innerHTML
  }

  buildPrintPayload = (): PrintPayload => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem
    const { from } = mailInfo
    const { meetingInfo } = this.state
    const { dateTimeFormat, timeFormat } = IFace.getTimeFormats()
    // body DOM 은 getSafeElement()로 sanitize 된 콘텐츠(의사요소 <style data-omw-pseudo> 포함).
    const bodyDiv = this.divFrameMap.fullBody || this.divFrameMap.body
    return {
      bodyHtml: bodyDiv != null ? this.getPrintBodyHtml(bodyDiv) : '',
      subject: mailInfo.subject,
      from,
      to: mailInfo.to || [],
      cc: mailInfo.cc || [],
      bcc: mailInfo.bcc || [],
      receivedTime: mailInfo.received_time,
      attachments: mailInfo.body?.attachments || [],
      dateTimeFormat,
      timeFormat,
      meetingInfo,
      fromDisplayHtml: getDisplayEmailHTMLStr(from),
      senderDisplayHtml: meetingInfo != null ? getDisplayEmailHTMLStr(meetingInfo.sender) : '',
      userEmail: getUserEmail() || '',
    }
  }

  onClickBtn = (cmd: string, e?: React.MouseEvent) => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    switch (cmd) {
      case 'reply':
        IFace.WebToNative.reply(mailInfo.id)
        break
      case 'reply-all':
      case 'replyAll':
        IFace.WebToNative.replyAll(mailInfo.id)
        break
      case 'forward':
        IFace.WebToNative.forward(mailInfo.id)
        break
      case 'forwardAsAttachment':
        IFace.WebToNative.forwardAsAttachment(mailInfo.id)
        break
      case 'openInNewWindow':
        IFace.WebToNative.openInNewWindow(mailInfo.id)
        break
      case 'viewMessageDetails':
        IFace.WebToNative.viewMessageDetails(mailInfo.id)
        break
      case 'redownload':
        IFace.WebToNative.redownloadMessage(mailInfo.id)
        break
      case 'download':
        IFace.WebToNative.saveEmail(mailInfo.id)
        break
      case 'inboxImportant':
        IFace.WebToNative.setPriorityInbox(mailInfo.id, 'other')
        break
      case 'inboxOther':
        IFace.WebToNative.setPriorityInbox(mailInfo.id, 'important')
        break
      case 'setRead':
        IFace.WebToNative.setRead(mailInfo.id, !mailInfo.read, true)
        break
      case 'delete':
        IFace.WebToNative.delete(mailInfo.id)
        break
      case 'newMail':
        IFace.WebToNative.newMail(mailInfo.id)
        break
      case 'sendAgain':
        IFace.WebToNative.sendAgain(mailInfo.id)
        break
      case 'print':
        // host 가 NineNative.print → requestPrintPayload 로 payload 를 되가져간다(payload 무전달).
        IFace.WebToNative.print(mailInfo.id)
        break
      case 'renewSecureAccess':
        IFace.WebToNative.secureSendRenewAccess(mailInfo.id)
        break
      case 'removeSecureAccess':
        IFace.WebToNative.secureSendRemoveAccess(mailInfo.id).then((ret: boolean) => {
          if (ret === true) {
            this.setState({ isSecureMail: false })
          }
        })
        break
      default:
    }

    if (e != null) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  setContainerRef = (ref: HTMLElement | null) => {
    if (ref != null) {
      this.containerRef = ref

      if (this.containerObserver == null) {
        this.containerObserver = {} as ContainerObserver

        this.containerObserver.onResize = () => {
          if (this.containerObserver!.resizeTimer != null) {
            cancelAnimationFrame(this.containerObserver!.resizeTimer)
          }
          this.containerObserver!.resizeTimer = requestAnimationFrame(() => {
            if (this.containerObserver!.element != null) {
              const { offsetWidth } = this.containerObserver!.element!

              let isLess400: boolean
              if (offsetWidth > 400) {
                isLess400 = false
              } else {
                isLess400 = true
              }

              if (this.prevLess400 !== isLess400) {
                this.prevLess400 = isLess400
                this.setState({
                  isLess400,
                })
              }

              // OMD-29: 폭이 원본 @media breakpoint 구간을 교차하면 media query 재반영 재계산.
              this.checkMediaQueryRecalc()
            }

            this.containerObserver!.resizeTimer = undefined
          }) as number
        }

        this.containerObserver.observer = new ResizeObserver(() => {
          this.containerObserver!.onResize()
        })

        this.containerObserver.clear = () => {
          const { observer, element, resizeTimer } = this.containerObserver!
          if (observer != null && element != null) {
            observer.disconnect()
            if (resizeTimer != null) {
              cancelAnimationFrame(resizeTimer)
            }
          }
        }
      }

      if (this.containerObserver.ref !== ref || this.containerObserver.element !== ref) {
        this.containerObserver.clear()

        this.containerObserver.element = ref
        this.containerObserver.ref = ref

        if (this.containerObserver.element != null) {
          this.containerObserver.onResize()
          this.containerObserver.observer!.observe(this.containerObserver.element, {
            box: 'border-box',
          })
        }
      }
    }
  }

  setActionsRef = (ref: HTMLElement | null) => {
    if (ref != null) {
      const refW = ref.offsetWidth
      if (refW > 0 && this.prevRefWidth !== refW) {
        this.setState({
          actionsWidth: refW,
        })
        this.prevRefWidth = refW
      }
    }
  }

  renderEmailItem = (item: EmailAddress, idx: number, array: EmailAddress[]) => {
    const endStr = idx < array.length - 1 ? ';' : ''

    const key = `${item.email}_${idx}`

    const { name, email } = item
    return getDisplayEmailElem({ name, email, endStr, key })
  }

  renderEmailPostfix = (
    isFold: boolean,
    viewCnt: number,
    array: EmailAddress[],
    toggle: () => void,
  ) => {
    const othersCnt = array.length - viewCnt
    if (othersCnt > 0 && array.length > 1) {
      let text = ''
      if (isFold === true) {
        if (othersCnt > 1) {
          text = i18n.t('email.moreMail.others', { count: othersCnt })
        } else {
          text = i18n.t('email.moreMail.other', { count: othersCnt })
        }
      } else {
        text = i18n.t('email.button.showLess')
      }
      return (
        <span
          className={classNames('foldBtn', 'others')}
          role="button"
          tabIndex={-1}
          onKeyDown={() => {}}
          onClick={(e) => {
            toggle()
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {text}
        </span>
      )
    }
    return ''
  }

  getEmailElems = (senders: EmailAddress[]) => {
    const othersCount = senders.length - 2
    return (
      <React.Fragment>
        {senders.slice(0, 2).map(({ name, email }) => (
          <span className="emailSet" key={email} data-email={email} data-name={name}>
            {name && name !== email && <span className="name">{name}</span>}
            <span className="email">&lt;{email}&gt;</span>
            {senders.length > 1 ? '; ' : ' '}
          </span>
        ))}
        {othersCount > 0 && (
          <span className="others" key="others">
            +{othersCount} other{othersCount > 1 && 's'}
          </span>
        )}
      </React.Fragment>
    )
  }

  onFocusChange = ({ focused, visible }: FocusInfo) => {
    // visible === true는 이미 펼쳐져 본문이 렌더된 상태를 뜻한다(setRead/notifyVisible은
    // 앞선 setFold(false) 또는 EmailContainer.select에서 이미 실행됨). 따라서 접혀서
    // 보이지 않는 검색 결과(focused && !visible)일 때만 펼친다 → 불필요한 재펼침/루프 방지.
    if (focused && !visible) {
      this.setFold(false)
    }
  }

  render() {
    const { avatar, infoElem, isDraft, hasExternalImg, isBlockedRes, actionsWidth, isLess400 } =
      this.state

    const { threadItem, hidden, selected, hasTitle, bodySearch } = this.props
    const { mailInfo } = threadItem

    const isFold = this.isFold()
    const clsProps = {
      fold: isFold,
      draft: isDraft,
      loading: this.loading === true,
      hiddenEmail: hidden === true,
      selected,
      scheduled: !!mailInfo.is_scheduled_email,
      // 읽지 않은 메일: 보낸 사람/받은 시간에 색·굵기 강조(ThreadList 와 동일).
      unread: mailInfo.read === false,
    }

    let elem: React.ReactNode = null
    if (mailInfo != null) {
      const {
        from,
        body,
        ai_summary: aiShortSummary,
        priority_inbox: priorityInbox,
        is_scheduled_email: isScheduledEmail,
      } = mailInfo
      const sender = from

      let subjectElem: React.ReactNode = null
      if (hasTitle !== false) {
        const title = mailInfo.subject
        const subject =
          title === undefined ? (
            <OmwIcon name="loading" spin />
          ) : (
            title || `(${i18n.t('email.desc.noSubject')})`
          )

        subjectElem = (
          <div className={classNames(styles.subjectWrapper)}>
            <div>
              <span className={classNames(styles.subject)}>{subject}</span>
            </div>
            {isScheduledEmail && (
              <div>
                <OmwButton
                  variant="text"
                  size="sm"
                  primary
                  onClick={() => IFace.WebToNative.reservationMailReschedule(mailInfo.id)}
                >
                  {i18n.t('email.title.reservationMailReschedule')}
                </OmwButton>
                <OmwButton
                  variant="text"
                  size="sm"
                  danger
                  onClick={() => IFace.WebToNative.reservationMailCancelSend(mailInfo.id)}
                >
                  {i18n.t('email.title.reservationMailCancelSend')}
                </OmwButton>
                <OmwButton
                  variant="text"
                  size="sm"
                  danger
                  onClick={() => IFace.WebToNative.reservationMailDelete(mailInfo.id)}
                >
                  {i18n.t('email.title.reservationMailDelete')}
                </OmwButton>
              </div>
            )}
          </div>
        )
      }

      const fromElem = (
        <TextFindableNode
          className="from"
          key="from"
          shallow={!bodySearch}
          onFocusChange={this.onFocusChange}
        >
          {isDraft && <span className="draftPrefix">[{i18n.t('email.desc.draft')}] </span>}

          <span className="emailSet" data-email={sender.email} data-name={sender.name}>
            {sender.name && sender.name !== sender.email && (
              <span className="name">{sender.name}</span>
            )}
            <span className="email">&lt;{sender.email}&gt;</span>
          </span>

          {uiExt.getPriorityInboxElem(this, priorityInbox, styles, isFold)}
        </TextFindableNode>
      )

      const summaryElem = (
        <TextFindableNode className="preview" disabled={!isFold} shallow>
          {aiShortSummary ? aiShortSummary : <Truncate>{mailInfo.summary}</Truncate>}
        </TextFindableNode>
      )

      const RecipientsWrapper: any = !isFold ? TextFindableNode : React.Fragment
      const hasTo = mailInfo.to != null && mailInfo.to.length > 0
      const recipientsElem = (
        <RecipientsWrapper>
          {hasTo && (
            <FoldableFlow
              className="to"
              key="to"
              items={mailInfo.to}
              renderItem={this.renderEmailItem}
              renderPrefix={() => {
                return `${i18n.t('email.field.to')}: `
              }}
              renderPostfix={this.renderEmailPostfix}
              emptyWidth={isLess400 !== true ? actionsWidth || 0 : 0}
              emptyLines={[0]}
            />
          )}
          {mailInfo.cc != null && mailInfo.cc.length > 0 && (
            <FoldableFlow
              className="cc"
              key="cc"
              items={mailInfo.cc}
              renderItem={this.renderEmailItem}
              renderPrefix={() => {
                return `${i18n.t('email.field.cc')}: `
              }}
              renderPostfix={this.renderEmailPostfix}
              emptyWidth={hasTo !== true && isLess400 !== true ? actionsWidth || 0 : 0}
              emptyLines={hasTo !== true ? [0] : undefined}
            />
          )}
          {mailInfo.bcc != null && mailInfo.bcc.length > 0 && (
            <FoldableFlow
              className="bcc"
              key="bcc"
              items={mailInfo.bcc}
              renderItem={this.renderEmailItem}
              renderPrefix={() => {
                return `${i18n.t('email.field.bcc')}: `
              }}
              renderPostfix={this.renderEmailPostfix}
            />
          )}
        </RecipientsWrapper>
      )

      const { dateTimeFormat } = IFace.getTimeFormats()

      const receivedAt = moment(mailInfo.received_time * 1000)
      const receiveDate = receivedAt.format(dateTimeFormat)

      const infoProps = {
        hidden:
          this.loading === true ||
          hasExternalImg === false ||
          isDraft === true ||
          isBlockedRes !== true ||
          infoElem == null,
      }

      const topIconsElem = uiExt.getTopIconsElem(this, isFold, styles)
      const actionElems = uiExt.getActionElems(this, styles)

      const { isReadReceipt, readReceiptStatus } = this.state
      let readReceiptChipElem: React.ReactNode = null
      if (isReadReceipt === true) {
        readReceiptChipElem =
          readReceiptStatus != null ? (
            <ReadReceiptChip
              status={readReceiptStatus}
              onClick={() => this.openReadReceiptDetail()}
              onRefresh={() => this.refreshReadReceipt(threadItem?.mailInfo?.id ?? '')}
            />
          ) : (
            <div className={classNames(styles.readReceiptLoading, 'readReceiptContainer')}>
              <OmwIcon name="loading" spin />
              <span>{i18n.t('readReceipt.chip.loading')}</span>
            </div>
          )
      }

      const detailElem = body != null ? uiExt.getDetailElem(this, styles) : null

      const spinElem =
        (infoElem == null && detailElem == null) || this.loading === true ? (
          <div className={classNames(styles.mailLoading)}>
            <OmwIcon name="loading" spin />
          </div>
        ) : null

      elem = (
        <React.Fragment>
          {subjectElem}
          <div
            ref={(ref) => {
              this.setContainerRef(ref)
            }}
            className={classNames(styles.container, 'emailViewContainer', {
              less400: isLess400,
            })}
            onClick={this.onClickContainer}
          >
            <div className={classNames(styles.mailHeaderWrapper)}>
              <div
                className={classNames(styles.mailHeader, 'mailHeader')}
                onClick={this.onClickHeader}
              >
                <div className={classNames(styles.avatar)} onClick={this.onClickAvatar}>
                  <OmwAvatar src={avatar} />
                </div>
                <div className={classNames(styles.desc)}>
                  <div className={classNames(styles.descInner)}>
                    {fromElem}
                    <div className={classNames(styles.extInfo)}>
                      <div className={classNames(styles.icons)}>{topIconsElem}</div>
                      {mailInfo.mailbox_name != null && mailInfo.mailbox_name !== '' && (
                        <OmwTooltip title={mailInfo.mailbox_fullname}>
                          <div className={classNames(styles.otherMailbox)}>
                            {mailInfo.mailbox_name}
                          </div>
                        </OmwTooltip>
                      )}
                      <div className="receivedAt">{receiveDate}</div>
                    </div>
                  </div>
                  <div className={classNames(styles.descInner)}>
                    <div className={classNames(styles.emailAddressInfo)}>
                      {summaryElem}
                      {recipientsElem}
                    </div>
                    <div
                      ref={(ref) => {
                        this.setActionsRef(ref)
                      }}
                      className={classNames(styles.actions)}
                    >
                      {actionElems}
                    </div>
                  </div>
                </div>
              </div>
              {readReceiptChipElem}
            </div>
            <div
              className={classNames(styles.mailBody, 'mailBody')}
              onClick={(e) => handleSchemaEventClick(bodyEventTarget(e.nativeEvent))}
            >
              <div className={classNames(styles.info, infoProps)}>{infoElem}</div>
              {detailElem}
              {spinElem}
            </div>
            {this.state.secureMailId != null &&
              !(
                this.state.secureSendExpired != null &&
                new Date(this.state.secureSendExpired) < new Date()
              ) && (
                <div className={styles.secureSendBannerContainer}>
                  {this.state.isSecureMail ? (
                    <ConfidentialBanner
                      secureSendExpired={this.state.secureSendExpired}
                      onRemoveAccess={() => this.onClickBtn('removeSecureAccess')}
                    />
                  ) : (
                    this.state.secureSendExpired != null && (
                      <BlockedBanner onRenewAccess={() => this.onClickBtn('renewSecureAccess')} />
                    )
                  )}
                </div>
              )}
          </div>
        </React.Fragment>
      )
    }

    return (
      <div
        className={classNames(styles.emailView, clsProps)}
        data-email-id={mailInfo.id}
        data-testid="mail-body"
      >
        {elem}
      </div>
    )
  }
}

export default forwardRef((props: EmailViewProps, ref) => {
  const emailViewRef = useRef<EmailView>(null)
  const { bodySearch } = useTextFinderContext()

  useEffect(() => {
    bodySearch && emailViewRef.current?.reqDetails(true)
  }, [bodySearch])

  return <EmailView {...props} bodySearch={bodySearch} ref={composeRefs(emailViewRef, ref)} />
})
